import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useChat } from '../context/ChatContext';
import { chatUsers, chatPresence, uploadChatFiles, mediaUrl, listMessages, sendMessage, addGroupMember, removeGroupMember, renameGroup, deleteGroupChat, reactMessage, recallMessage, editMessage, deleteMessageForMe, remindMessage, muteConversation, listObserverGroups, pinMessage, unpinMessage, pinConversation, votePoll, closePoll, createChatTask, acceptChatTask, completeChatTask, deleteChatTask, REACTION_EMOJIS } from '../api/chat';
import ChatTaskModal from './ChatTaskModal';
import ChatTasksPanel, { openTaskCount } from './ChatTasksPanel';
import { onAttachmentClick } from '../download';
import { fmtSize } from '../utils/chatFiles';
import { linkifyText } from '../utils/linkify';
import { createNote } from '../api/notes';
import { getSocket, emitTyping, emitBuzz } from '../socket';
import { pushBackHandler } from '../backButton';
import { playBuzzSound } from '../utils/sound';
import { useToast } from './Toast';
import EmojiPicker from './EmojiPicker';
import PollCard from './PollCard';
import PollComposer from './PollComposer';
import { Sticker } from '../stickers';
import ImageLightbox from './ImageLightbox';
import { CustomerConversationList, CustomerChatWindow, NewCustomerChat, canMuteKh } from './CustomerChat';
import { useAuth } from '../context/AuthContext';
import { custMuteChannel, custMuteAll } from '../api/customerChat';
import { SVC_ORDER, SVC_LABEL } from '../utils/custChannels';

// Tin chỉ gồm emoji (ngắn) -> render cỡ lớn như sticker.
const EMOJI_ONLY = /^(?:[\p{Extended_Pictographic}‍️⃣\u{1F3FB}-\u{1F3FF}]|\s)+$/u;
function isBigEmoji(t) {
  if (!t) return false;
  const s = t.trim();
  if (!s || !EMOJI_ONLY.test(s)) return false;
  return [...s].filter((c) => !/\s/.test(c)).length <= 6;
}

// Hiển thị 1 đính kèm trong bong bóng tin nhắn (ảnh xem trực tiếp, tệp tải về).
// Ảnh = type image/* HOẶC đuôi file là ảnh (attachment thiếu type -> đừng coi ảnh là tệp).
const isImageAtt = (a) => (a.type || '').startsWith('image/') || /\.(jpe?g|png|gif|webp|bmp|heic|heif|svg|avif)(\?|#|$)/i.test(a.name || a.url || a.data || '');
function AttachmentView({ a }) {
  const src = mediaUrl(a.url || a.data);
  // Ưu tiên BẢN THU NHỎ (nếu có thumbUrl) cho bong bóng chat -> tải/giải mã nhẹ hơn nhiều ảnh gốc;
  // data-full giữ ảnh GỐC cho lightbox. decoding=async: giải mã ngoài luồng chính -> cuộn đỡ khựng.
  const thumb = a.thumbUrl ? mediaUrl(a.thumbUrl) : '';
  if (isImageAtt(a)) {
    // Bấm vào ảnh -> mở lightbox in-app (xử lý bằng delegation ở .chatmsgs), không mở tab mới.
    return (
      <img
        className="chat-att-img"
        src={thumb || src}
        data-full={src}
        alt={a.name}
        title="Bấm để xem to"
        style={{ cursor: 'zoom-in' }}
        loading="lazy"
        decoding="async"
        onError={(e) => {
          const el = e.currentTarget;
          // Bản thu nhỏ hỏng -> lùi về ảnh gốc ĐÚNG một lần rồi mới báo vỡ (chống lặp onError).
          if (thumb && el.dataset.retry !== '1') { el.dataset.retry = '1'; el.src = src; return; }
          el.classList.add('chat-att-img--broken'); el.removeAttribute('title'); el.alt = 'Ảnh không tải được';
        }}
      />
    );
  }
  return (
    <a className="chat-att-file" href={src} download={a.name} target="_blank" rel="noreferrer" onClick={onAttachmentClick(src, a.name)}>
      📎 <span className="chat-att-name">{a.name}</span>
      <span className="chat-att-size">{fmtSize(a.size)}</span>
    </a>
  );
}

const initials = (name) => (name || '?').trim().split(/\s+/).slice(-1)[0]?.[0]?.toUpperCase() || '?';
// Ruột khung avatar: có ảnh đại diện -> ảnh tròn phủ kín khung .chat-avatar; không -> chữ cái đầu.
const AvaImg = ({ src, name }) => (src ? <img className="chat-avatar-img" src={src} alt="" /> : initials(name));
const fmtTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const today = new Date();
  const sameDay = dt.toDateString() === today.toDateString();
  if (sameDay) return dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  const p = (n) => String(n).padStart(2, '0');
  return `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
};
// Giờ DƯỚI TIN NHẮN: hôm nay chỉ HH:MM; tin NGÀY CŨ kèm cả ngày tháng (năm chỉ hiện khi khác năm).
// Tách khỏi fmtTime vì fmtTime còn dùng ở DANH SÁCH hội thoại (cột hẹp, giờ+ngày sẽ tràn).
const fmtMsgTime = (d) => {
  if (!d) return '';
  const dt = new Date(d);
  const now = new Date();
  const hm = dt.toLocaleTimeString('vi', { hour: '2-digit', minute: '2-digit' });
  if (dt.toDateString() === now.toDateString()) return hm;
  const p = (n) => String(n).padStart(2, '0');
  const dm = `${p(dt.getDate())}/${p(dt.getMonth() + 1)}`;
  return dt.getFullYear() === now.getFullYear() ? `${hm} · ${dm}` : `${hm} · ${dm}/${dt.getFullYear()}`;
};

// Nhãn ngày cho vạch phân cách giữa các ngày trong đoạn chat: Hôm nay / Hôm qua / Thứ …, DD/MM/YYYY.
const fmtDayLabel = (d) => {
  const dt = new Date(d);
  const now = new Date();
  const dayMs = 24 * 60 * 60 * 1000;
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diff = Math.round((startOf(now) - startOf(dt)) / dayMs);
  if (diff === 0) return 'Hôm nay';
  if (diff === 1) return 'Hôm qua';
  const p = (n) => String(n).padStart(2, '0');
  const base = `${p(dt.getDate())}/${p(dt.getMonth() + 1)}/${dt.getFullYear()}`;
  if (diff > 1 && diff < 7) {
    const wd = ['Chủ nhật', 'Thứ 2', 'Thứ 3', 'Thứ 4', 'Thứ 5', 'Thứ 6', 'Thứ 7'][dt.getDay()];
    return `${wd}, ${base}`;
  }
  return base;
};

// Gộp 40 tin mới nhất từ server vào danh sách hiện có mà GIỮ tin cũ đã tải thêm + tin đang gửi/lỗi.
// Dùng chung cho: poll dự phòng 5s và lúc socket (tái) kết nối — trước đây reconnect thay CẢ danh sách
// (mất tin cũ đang đọc + tin đang gửi). Trả về prev nếu không đổi để React khỏi render lại.
function mergeLatest(prev, msgs) {
  const serverIds = new Set(msgs.map((m) => String(m._id)));
  const serverClientIds = new Set(msgs.map((m) => m.clientId).filter(Boolean)); // server GIỜ trả clientId
  // Bỏ tin tạm ĐÃ có bản thật trên server — khớp theo _id HOẶC clientId. Nhờ vậy khi socket lỡ event
  // mà HTTP gửi bị treo/timeout, refresh (list có clientId) tự gỡ "Đang gửi.../Gửi lỗi" trong ~5s.
  const pending = prev.filter((m) => m.clientId && (m.pending || m.failed)
    && !serverIds.has(String(m._id)) && !serverClientIds.has(m.clientId));
  const oldestSrv = msgs.length ? new Date(msgs[0].createdAt).getTime() : 0;
  // GIỮ các tin CŨ đã tải (cuộn lên) nằm trước lô mới. Tin tạm nhận diện bằng pending/failed (KHÔNG bằng
  // clientId — server trả clientId cho cả tin thật). Trước lọc "!m.clientId" -> tin thật có clientId bị RỚT
  // mỗi nhịp poll -> tải tin cũ lên rồi 5s sau lại mất.
  const olderKept = prev.filter((m) => !m.pending && !m.failed && !serverIds.has(String(m._id)) && m.createdAt && new Date(m.createdAt).getTime() < oldestSrv);
  const next = [...olderKept, ...msgs, ...pending];
  if (next.length === prev.length && next.every((m, i) => String(m._id) === String(prev[i]?._id))) return prev;
  return next;
}

// Bỏ dấu tiếng Việt để tìm hội thoại không cần gõ dấu ("hoi tra chanh" ra "Hội trà chanh").
export const foldVi = (s) =>
  String(s || '').normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/đ/g, 'd').replace(/Đ/g, 'D').toLowerCase();

/* ===== Kéo–thả bong bóng chat (giống ChatPopup bên cs.globalcargo) =====
   Nút chat nổi mặc định neo góc phải–dưới bằng CSS right/bottom. Khi người dùng bắt đầu kéo,
   ta đọc vị trí THẬT của nút rồi chuyển sang toạ độ left/top tuyệt đối (không thì con trỏ sẽ
   "nhảy" so với nút), và nhớ lại trong localStorage để lần sau vào vẫn đúng chỗ.
   Phân biệt KÉO với BẤM bằng ngưỡng 5px — kéo xong thả tay KHÔNG được mở chat. */
const FAB_POS_KEY = 'ft_chatfab_pos';
const FAB_SIZE = 56; // khớp width/height .chatfab
const FAB_EDGE = 8; // lề tối thiểu so với mép màn hình
const FAB_DRAG_MIN = 5; // di chuyển quá ngần này px thì coi là kéo, không phải bấm

const clampFab = (p) => ({
  x: Math.max(FAB_EDGE, Math.min(p.x, window.innerWidth - FAB_SIZE - FAB_EDGE)),
  y: Math.max(FAB_EDGE, Math.min(p.y, window.innerHeight - FAB_SIZE - FAB_EDGE)),
});

function useFabDrag() {
  const ref = useRef(null);
  const [pos, setPos] = useState(() => {
    if (typeof window === 'undefined') return null;
    try {
      const s = JSON.parse(localStorage.getItem(FAB_POS_KEY) || 'null');
      return s && Number.isFinite(s.x) && Number.isFinite(s.y) ? clampFab(s) : null;
    } catch { return null; } // null = giữ nguyên vị trí mặc định của CSS
  });
  const [dragging, setDragging] = useState(false);
  const movedRef = useRef(false);
  const offRef = useRef({ x: 0, y: 0 });
  const startRef = useRef({ x: 0, y: 0 });
  const hasPos = !!pos;

  const begin = useCallback((cx, cy) => {
    const r = ref.current?.getBoundingClientRect();
    if (!r) return;
    offRef.current = { x: cx - r.left, y: cy - r.top };
    startRef.current = { x: cx, y: cy };
    movedRef.current = false;
    setPos({ x: r.left, y: r.top });
    setDragging(true);
  }, []);

  useEffect(() => {
    if (!dragging) return undefined;
    const move = (cx, cy) => {
      if (!movedRef.current && Math.hypot(cx - startRef.current.x, cy - startRef.current.y) > FAB_DRAG_MIN) {
        movedRef.current = true;
      }
      setPos(clampFab({ x: cx - offRef.current.x, y: cy - offRef.current.y }));
    };
    const onMouseMove = (e) => { e.preventDefault(); move(e.clientX, e.clientY); };
    const onTouchMove = (e) => {
      const t = e.touches[0];
      if (!t) return;
      e.preventDefault(); // chặn cuộn trang trong lúc kéo trên cảm ứng
      move(t.clientX, t.clientY);
    };
    const end = () => setDragging(false);
    document.addEventListener('mousemove', onMouseMove, { passive: false });
    document.addEventListener('mouseup', end);
    document.addEventListener('touchmove', onTouchMove, { passive: false });
    document.addEventListener('touchend', end);
    document.addEventListener('touchcancel', end);
    const prevSel = document.body.style.userSelect;
    document.body.style.userSelect = 'none';
    return () => {
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', end);
      document.removeEventListener('touchmove', onTouchMove);
      document.removeEventListener('touchend', end);
      document.removeEventListener('touchcancel', end);
      document.body.style.userSelect = prevSel;
    };
  }, [dragging]);

  // Ghi nhớ khi ĐÃ thả tay (đang kéo mà ghi thì mỗi khung hình một lần ghi localStorage).
  useEffect(() => {
    if (dragging || !pos) return;
    try { localStorage.setItem(FAB_POS_KEY, JSON.stringify(pos)); } catch { /* hết quota / chặn storage -> bỏ qua */ }
  }, [dragging, pos]);

  // Thu nhỏ cửa sổ trình duyệt -> kéo nút về lại trong màn hình.
  useEffect(() => {
    if (!hasPos) return undefined;
    const h = () => setPos((p) => (p ? clampFab(p) : p));
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [hasPos]);

  return {
    ref,
    dragging,
    style: pos ? { left: pos.x, top: pos.y, right: 'auto', bottom: 'auto' } : undefined,
    // Vừa kéo xong thì nuốt cú click để không mở chat ngoài ý muốn.
    isClick: () => !movedRef.current,
    onMouseDown: (e) => { if (e.button === 0) begin(e.clientX, e.clientY); },
    onTouchStart: (e) => { const t = e.touches[0]; if (t) begin(t.clientX, t.clientY); },
  };
}

export default function ChatWidget() {
  const chat = useChat();
  const [vw, setVw] = useState(typeof window !== 'undefined' ? window.innerWidth : 1280);
  const [q, setQ] = useState(''); // tìm hội thoại trong danh sách của widget
  const fab = useFabDrag(); // bong bóng chat kéo–thả được, nhớ vị trí
  useEffect(() => {
    const h = () => setVw(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  // Nút BACK CỨNG của Android khi chat đang mở. Trên điện thoại chat chiếm TRỌN màn hình
  // (CSS .chatpanel inset:0), nên không bắt back thì bấm back = THOÁT APP giữa lúc đang
  // đọc tin. Thứ tự lùi: đang trong hội thoại -> về danh sách; đang ở danh sách -> đóng chat.
  // Đọc state qua ref để chỉ đăng ký MỘT lần mỗi lần mở — đăng ký lại liên tục sẽ đẩy
  // handler này lên trên cả modal đang mở, khiến back đóng nhầm lớp.
  const backRef = useRef(null);
  backRef.current = chat;
  const chatOpen = !!(chat && chat.me && chat.open);
  useEffect(() => {
    if (!chatOpen) return undefined;
    return pushBackHandler(() => {
      const c = backRef.current;
      if (!c) return;
      const top = c.windows[c.windows.length - 1];
      if (top) c.closeWindow(top.id);
      else c.setOpen(false);
    });
  }, [chatOpen]);

  if (!chat || !chat.me) return null;
  const { open, setOpen, unreadConvs, windows, view, openConv } = chat;

  // Hiện TỐI ĐA số cửa sổ hội thoại còn VỪA bề ngang (không đè nhau; ưu tiên cửa sổ mới nhất).
  // ≤980px: chế độ full màn (CSS lo), chỉ cần 1 cửa sổ.
  const GAP = 12; // khoảng cách giữa các cửa sổ (khớp .chatdock gap)
  // Bề rộng cửa sổ = kích thước user tự chỉnh (nếu có), mặc định 360.
  const savedW = (() => {
    try { const s = JSON.parse(localStorage.getItem('ft_chat_size') || 'null'); return s && s.w ? s.w : null; } catch { return null; }
  })();
  const convW = (savedW || 360) + GAP;
  const listW = 340 + GAP; // cột danh sách tin nhắn (cố định bên phải)
  const MARGIN = 40; // lề an toàn trái — cửa sổ ĐƯỢC PHÉP che sidebar (màn hẹp quý từng px;
  // trước đây chừa hẳn 250px cho sidebar nên laptop 1366px chỉ mở nổi 1 cửa sổ)
  const fit =
    vw <= 980 ? 1 : Math.max(1, Math.floor((vw - 22 - listW - MARGIN) / convW)); // trừ lề phải, cột danh sách, lề trái
  const visibleWindows = windows.slice(-fit);
  // Hội thoại đang mở nhưng KHÔNG đủ chỗ hiện -> thu thành chip tròn xếp dọc cạnh trái dock;
  // bấm chip là đưa lên trước (openConv tự đẩy về cuối mảng = vị trí hiển thị).
  const hiddenWindows = vw <= 980 ? [] : windows.slice(0, -fit);

  return (
    <>
      <ChatToasts />
      {!open && (
        <button
          ref={fab.ref}
          className={'chatfab' + (fab.dragging ? ' dragging' : '')}
          style={fab.style}
          onClick={() => { if (fab.isClick()) setOpen(true); }}
          onMouseDown={fab.onMouseDown}
          onTouchStart={fab.onTouchStart}
          title="Tin nhắn (kéo để đổi chỗ)"
        >
          💬
          {unreadConvs > 0 && <span className="chatfab-badge">{unreadConvs > 99 ? '99+' : unreadConvs}</span>}
        </button>
      )}
      {open && (
        <div className="chatdock">
          {/* Chip các hội thoại mở nhưng hết chỗ hiện — bấm để đưa lên trước */}
          {hiddenWindows.length > 0 && (
            <div className="chatdock-hidden">
              {hiddenWindows.map((c) => (
                <button key={c.id} className="chatdock-chip" title={c.name || 'Hội thoại'} onClick={() => openConv(c)}>
                  {c.type === 'group' ? '👥' : <AvaImg src={c.other?.avatar} name={c.name} />}
                </button>
              ))}
            </div>
          )}
          {/* Các cửa sổ hội thoại mở ra bên trái cột quản lý (chỉ hiện số vừa màn hình).
              kind='customer' -> cửa sổ chat KHÁCH HÀNG (bidnhanh), còn lại là chat nội bộ. */}
          {visibleWindows.map((c, i) =>
            c.kind === 'customer' ? (
              <CustomerChatWindow key={c.id} conv={c} front={i === visibleWindows.length - 1}
                onSwitchChannel={(ch) => chat.switchWindowConv(c.id, ch)} />
            ) : (
              <ChatWindow key={c.id} conv={c} front={i === visibleWindows.length - 1} />
            )
          )}
          {/* Cột quản lý tin nhắn — cố định ngoài cùng bên phải */}
          <div className="chatpanel chatpanel-list">
            <ListHeader />
            {view === 'list' && <ChatTabs />}
            {/* Ô TÌM HỘI THOẠI (không cần gõ dấu) — chỉ ở màn danh sách. */}
            {view === 'list' && (
              <div className="chatlist-search">
                <i className="ti ti-search" aria-hidden="true" />
                <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Tìm hội thoại…" />
                {q && <button type="button" className="chatlist-search-x" title="Xoá tìm" onClick={() => setQ('')}>×</button>}
              </div>
            )}
            <div className="chatbody">
              {view === 'new' ? <NewChat /> : view === 'newCust' ? <NewCustomerChat onDone={() => chat.setView('list')} /> : view === 'settings' ? <Settings /> : view === 'presence' ? <PresencePanel /> : chat.tab === 'customer' ? <CustomerConversationList q={q} /> : <ConversationList q={q} />}
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ChatToasts() {
  const { toasts, dismissToast, openConvById } = useChat();
  if (!toasts?.length) return null;
  return (
    <div className="chat-toasts">
      {toasts.map((t) => (
        <div
          key={t.id}
          className="chat-toast"
          onClick={() => {
            openConvById(t.convId, t.kind); // kind='customer' -> mở bên tab Khách hàng
            dismissToast(t.id);
          }}
        >
          <div className="chat-toast-ic">💬</div>
          <div className="chat-toast-main">
            <div className="chat-toast-title">{t.title}</div>
            <div className="chat-toast-body">{t.body}</div>
          </div>
          <button
            className="chat-toast-x"
            onClick={(e) => {
              e.stopPropagation();
              dismissToast(t.id);
            }}
          >
            ×
          </button>
        </div>
      ))}
    </div>
  );
}

function ListHeader() {
  const { view, setView, setOpen, tab } = useChat();
  const title = view === 'new' ? 'Cuộc trò chuyện mới' : view === 'newCust' ? 'Nhắn khách hàng mới' : view === 'settings' ? 'Cài đặt thông báo' : view === 'presence' ? 'Trạng thái nhân viên' : 'Tin nhắn';
  return (
    <div className="chathead">
      {view !== 'list' && (
        <button className="chathead-btn" onClick={() => setView('list')} title="Quay lại danh sách">
          ‹
        </button>
      )}
      <div className="chathead-title">{title}</div>
      {view === 'list' && (
        <>
          {tab !== 'customer' && (
            <button className="chathead-btn" onClick={() => setView('presence')} title="Ai đang online / offline bao lâu">
              👥
            </button>
          )}
          <button className="chathead-btn" onClick={() => setView('settings')} title="Cài đặt thông báo">
            ⚙
          </button>
          <button className="chathead-btn" onClick={() => setView(tab === 'customer' ? 'newCust' : 'new')}
            title={tab === 'customer' ? 'Nhắn khách hàng mới (chủ động, không chờ KH nhắn trước)' : 'Tạo cuộc trò chuyện'}>
            ✎
          </button>
        </>
      )}
      <button className="chathead-btn" onClick={() => setOpen(false)} title="Đóng">
        ×
      </button>
    </div>
  );
}

// 2 tab đầu danh sách: Nội bộ (chat nhân viên) | Khách hàng (chat KH từ bidnhanh). Badge = số tin chưa đọc.
function ChatTabs() {
  const { tab, setTab, internalUnread, custUnread, me } = useChat();
  const TabBtn = ({ k, label, n }) => (
    <button type="button" className={'chattab' + (tab === k ? ' on' : '')} onClick={() => setTab(k)}>
      {label}
      {n > 0 && <span className="chattab-badge">{n > 99 ? '99+' : n}</span>}
    </button>
  );
  return (
    <div className="chattabs">
      <TabBtn k="internal" label="Nội bộ" n={internalUnread} />
      {me?.role !== 'DT' && <TabBtn k="customer" label="Khách hàng" n={custUnread} />}
    </div>
  );
}

// HỘI THOẠI GHIM lên đầu danh sách (riêng mỗi người, tối đa 3) — trong nhóm ghim/không ghim
// vẫn giữ thứ tự tin mới nhất (sort ổn định).
export const pinnedFirst = (arr) => [...arr].sort((a, b) => (a.pinned === b.pinned ? 0 : a.pinned ? -1 : 1));

function ConversationList({ q = '' }) {
  const { me, convs, openConv, setView, windows, onlineIds, patchConv, drafts } = useChat();
  const togglePinConv = async (e, c) => {
    e.stopPropagation(); // đừng mở cửa sổ khi chỉ định ghim
    try {
      const r = await pinConversation(c.id, !c.pinned);
      patchConv(c.id, { pinned: r.pinned });
    } catch (err) { alert(err?.response?.data?.message || 'Ghim thất bại'); }
  };
  // Lọc theo TÊN hội thoại (bỏ dấu) khi có từ khoá tìm.
  const kw = foldVi(q).trim();
  const shown = kw ? convs.filter((c) => foldVi(c.name).includes(kw)) : convs;
  if (!convs.length)
    return (
      <div className="chatempty">
        Chưa có cuộc trò chuyện.
        <br />
        <button className="btn sm" style={{ marginTop: 10 }} onClick={() => setView('new')}>
          ✎ Bắt đầu chat
        </button>
      </div>
    );
  if (kw && !shown.length) return <div className="chatempty">Không có hội thoại nào khớp “{q.trim()}”.</div>;
  return (
    <div className="chatlist">
      {pinnedFirst(shown).map((c) => {
        const opened = windows.some((w) => String(w.id) === String(c.id));
        const online = c.type === 'direct' && c.other?.id && onlineIds?.has(String(c.other.id));
        return (
          <div
            key={c.id}
            className={'chatlist-row' + (c.unread ? ' unread' : '') + (opened ? ' active' : '')}
            onClick={() => openConv(c)}
          >
            <div className="chatlist-ava">
              <div className={'chat-avatar' + (c.type === 'group' ? ' group' : '')}>{c.type === 'group' ? '👥' : <AvaImg src={c.other?.avatar} name={c.name} />}</div>
              {online && <span className="chat-online-dot" title="Đang online" />}
              {c.unread > 0 && <span className="chatlist-dot" />}
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name">{c.pinned ? '📌 ' : ''}{c.name}</span>
                {drafts?.[c.id]
                  ? <span className="chatlist-time" style={{ color: 'var(--danger)', fontWeight: 600 }}>Chưa gửi</span>
                  : <span className="chatlist-time">{fmtTime(c.lastMessage?.at)}</span>}
              </div>
              <div className="chatlist-preview">{drafts?.[c.id] || c.lastMessage?.text || 'Bắt đầu trò chuyện…'}</div>
            </div>
            {c.unread > 0 && <span className="chatlist-badge">{c.unread}</span>}
            <button type="button" className={'chatlist-pinbtn' + (c.pinned ? ' on' : '')}
              title={c.pinned ? 'Bỏ ghim hội thoại' : 'Ghim hội thoại lên đầu (tối đa 3)'}
              onClick={(e) => togglePinConv(e, c)}>📌</button>
          </div>
        );
      })}
      {me?.role === 'GD' && <ObserverGroups openConv={openConv} windows={windows} />}
    </div>
  );
}

// (Giám đốc) GIÁM SÁT NHÓM: liệt kê mọi nhóm KHÔNG có mình — bấm mở ở chế độ CHỈ XEM.
// Tải sẵn để biết CÓ nhóm nào để giám sát không. KHÔNG có (đang ở trong tất cả các nhóm) -> ẨN HẲN
// mục này (VD tài khoản Cố vấn ở trong mọi nhóm khỏi thấy phần trống vô nghĩa).
function ObserverGroups({ openConv, windows }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(null); // null = chưa tải
  useEffect(() => {
    let alive = true;
    listObserverGroups().then((r) => alive && setItems(r || [])).catch(() => alive && setItems([]));
    return () => { alive = false; };
  }, []);
  if (!items || !items.length) return null; // chưa tải xong / không có nhóm nào -> không hiện gì
  return (
    <div className="chat-observer-sec">
      <button type="button" className="chat-observer-head" onClick={() => setOpen((v) => !v)}>
        👁 Giám sát nhóm (không có bạn) {open ? '▾' : '▸'}
      </button>
      {open && items.map((c) => {
        const opened = windows.some((w) => String(w.id) === String(c.id));
        return (
          <div key={c.id} className={'chatlist-row' + (opened ? ' active' : '')} onClick={() => openConv({ ...c, observer: true })}>
            <div className="chatlist-ava"><div className="chat-avatar group">👁</div></div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name">{c.name}</span>
                <span className="chatlist-time">{fmtTime(c.lastMessage?.at)}</span>
              </div>
              <div className="chatlist-preview">{(c.members || []).length} thành viên · {c.lastMessage?.text || 'Chưa có tin'}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// "Offline bao lâu" từ mốc lastSeenAt tới hiện tại.
function sinceLabel(lastSeenAt) {
  if (!lastSeenAt) return 'chưa truy cập';
  const diff = Date.now() - new Date(lastSeenAt).getTime();
  if (diff < 60_000) return 'vừa xong';
  const mins = Math.floor(diff / 60_000);
  if (mins < 60) return `${mins} phút trước`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} giờ${mins % 60 ? ` ${mins % 60}p` : ''} trước`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} ngày trước`;
  return new Date(lastSeenAt).toLocaleDateString('vi-VN');
}

// Panel "Trạng thái nhân viên": ai đang online / offline bao lâu. Online realtime theo onlineIds,
// mốc offline lấy từ API (lastSeenAt). Có thanh tìm theo tên.
function PresencePanel() {
  const { onlineIds } = useChat();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [, force] = useState(0);
  useEffect(() => {
    let alive = true;
    chatPresence().then((d) => { if (alive) setRows(d.rows || []); }).catch(() => {}).finally(() => alive && setLoading(false));
    // Cập nhật nhãn "x phút trước" mỗi 30s cho tươi.
    const t = setInterval(() => force((n) => n + 1), 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  // Gộp trạng thái online realtime từ onlineIds; sắp online lên đầu.
  const list = rows
    .map((u) => ({ ...u, online: !u.resigned && (onlineIds.has(String(u.id)) || u.online) })) // đã nghỉ: không bao giờ online
    .filter((u) => !q.trim() || u.name.toLowerCase().includes(q.trim().toLowerCase()))
    .sort((a, b) => (a.resigned === b.resigned ? 0 : a.resigned ? 1 : -1) // đã nghỉ chót bảng
      || (a.online === b.online ? 0 : a.online ? -1 : 1)
      || (new Date(b.lastSeenAt || 0) - new Date(a.lastSeenAt || 0)));
  const onlineCount = list.filter((u) => u.online).length;
  const resignedCount = list.filter((u) => u.resigned).length;
  return (
    <div className="chatlist">
      <div style={{ padding: '8px 10px' }}>
        <input className="chat-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Tìm nhân viên…" style={{ width: '100%' }} />
        <div className="small muted" style={{ marginTop: 6 }}>🟢 {onlineCount} đang online · {list.length - onlineCount - resignedCount} offline{resignedCount ? ` · ${resignedCount} đã nghỉ` : ''}</div>
      </div>
      {loading ? <div className="chatempty">Đang tải…</div> : !list.length ? <div className="chatempty">Không có nhân viên.</div> : (
        list.map((u) => (
          <div key={u.id} className="chatlist-row" style={{ cursor: 'default' }}>
            <div className="chatlist-ava">
              <div className="chat-avatar"><AvaImg src={u.avatar} name={u.name} /></div>
              <span style={{ position: 'absolute', right: 0, bottom: 0, width: 11, height: 11, borderRadius: '50%', border: '2px solid var(--panel)', background: u.online ? '#22c55e' : '#9ca3af' }} />
            </div>
            <div className="chatlist-main">
              <div className="chatlist-top">
                <span className="chatlist-name" style={u.resigned ? { opacity: 0.6 } : undefined}>{u.name}</span>
                <span className="chatlist-time" style={{ color: u.resigned ? 'var(--danger)' : u.online ? '#16a34a' : 'var(--muted)' }}>
                  {u.resigned ? 'Đã nghỉ việc' : u.online ? 'Online' : sinceLabel(u.lastSeenAt)}
                </span>
              </div>
              <div className="chatlist-preview">{(u.departments || []).join(', ') || u.role}</div>
            </div>
          </div>
        ))
      )}
    </div>
  );
}

const MENTION_ALL = 'Tất cả';
// Thu hồi tin: chỉ trong 10 phút kể từ khi gửi (khớp RECALL_WINDOW_MS ở backend).
const RECALL_WINDOW_MS = 10 * 60 * 1000;
const canRecall = (m, myId) => !m.recalled && !m.pending && !m.failed && String(m.sender?._id) === myId
  && Date.now() - new Date(m.createdAt).getTime() < RECALL_WINDOW_MS;
// Nhắc lại thông báo: tin CỦA MÌNH, chưa thu hồi, và có TAG ai đó (nhóm) hoặc là chat riêng 1-1.
const canRemind = (m, myId, isGroup) => !m.recalled && !m.pending && !m.failed && String(m.sender?._id) === myId
  && (!isGroup || m.mentionAll || (m.mentions || []).length > 0);
// Sửa tin: tin CHỮ của mình, chưa thu hồi, đã gửi xong (không sửa sticker/poll/chỉ-tệp).
const canEdit = (m, myId) => !m.recalled && !m.pending && !m.failed && String(m.sender?._id) === myId && !!m.text && !m.sticker && !m.poll;
const ROLE_LABEL = { GD: 'Giám đốc', CV: 'Cố vấn', TP: 'Trưởng phòng', NV: 'Nhân viên' };

// Mũi tên kép hướng lên (nút "tới tin chưa đọc" / "nhắc đến bạn").
const ChevronsUp = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M17 11l-5-5-5 5" /><path d="M17 18l-5-5-5 5" />
  </svg>
);

// Tô màu các đoạn @tên trong nội dung tin nhắn. redNames = tên tô ĐỎ (tên MÌNH được tag / @Tất cả) — dễ thấy;
// còn lại tô xanh như cũ (CSS .chat-mention-me).
function renderWithMentions(text, names, redNames = []) {
  if (!names.length) return linkifyText(text); // không có mention -> vẫn nhận diện link
  const sorted = [...names].sort((a, b) => b.length - a.length).map((n) => n.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
  const re = new RegExp('@(' + sorted.join('|') + ')', 'g');
  const redSet = new Set(redNames);
  const out = [];
  let last = 0;
  let m;
  let i = 0;
  while ((m = re.exec(text))) {
    if (m.index > last) out.push(linkifyText(text.slice(last, m.index), `s${i}`));
    out.push(
      <span className={redSet.has(m[1]) ? 'chat-mention chat-mention-me' : 'chat-mention'} key={`m${i++}`}>
        {m[0]}
      </span>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) out.push(linkifyText(text.slice(last), 'end'));
  return out;
}

// Một cửa sổ hội thoại độc lập (mở được nhiều cái cùng lúc).
// export: trang "Tin nhắn" (ChatPage) nhúng lại cửa sổ này làm cột giữa — mọi tính năng dùng chung.
// onTitleClick (chỉ ChatPage truyền): bấm TÊN trên đầu khung -> mở/đóng bảng thông tin bên phải.
export function ChatWindow({ conv, front, onTitleClick, onBack }) {
  const { me, convs, refresh, subscribeMessages, subscribeRead, setConvMinimized, patchConv, startDirect, onlineIds, bumpConv, closeWindow, removeConvsLocal, setOpen, setDraft, setComposerFocused, buzzSignal, wakeSignal } = useChat();
  const isMobile = () => typeof window !== 'undefined' && window.innerWidth <= 980;
  const toast = useToast();
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(true);
  // Đang xem CỬA SỔ LỊCH SỬ (nhảy tới 1 tin cũ theo mốc) -> tin MỚI NHẤT có thể chưa nạp;
  // nút "↓ Tin mới nhất" sẽ tải lại tin mới nhất để quay về hôm nay (kiểu Facebook).
  const jumpedRef = useRef(false);
  // Giữ NỘI DUNG ĐANG SOẠN DỞ theo từng hội thoại: lỡ chuyển nhóm rồi quay lại vẫn còn.
  const [text, setText] = useState(() => { try { return localStorage.getItem('ft_draft_' + conv.id) || ''; } catch { return ''; } });
  // Textarea UNCONTROLLED (defaultValue + ref): React KHÔNG tự ghi 'value' -> cửa sổ re-render (tin
  // đến / "đang gõ" / tick) giữa lúc gõ dấu KHÔNG cắt IME (Telex) -> hết "gõ tiếng Việt mất dấu".
  // State `text` vẫn theo nội dung (nút Gửi + nháp + tự cao) qua onChange; đổi bằng CODE thì dùng putText.
  const initialTextRef = useRef(null); // nháp lúc MOUNT -> defaultValue
  if (initialTextRef.current === null) initialTextRef.current = text;
  const [men, setMen] = useState(null); // { at, q } khi đang gõ @
  const [atts, setAtts] = useState([]); // đính kèm đang chờ gửi
  const [replyTo, setReplyTo] = useState(null); // { id, senderId, senderName, text } tin đang trả lời
  const [imgView, setImgView] = useState(null); // ảnh đang xem to (lightbox) — { src, name }
  const [forwardMsg, setForwardMsg] = useState(null); // tin đang chọn để CHUYỂN TIẾP (mở modal chọn hội thoại)
  const [taskMsg, setTaskMsg] = useState(null); // tin đang chọn để TẠO CÔNG VIỆC (mở ô tạo việc)
  const [dragOver, setDragOver] = useState(false);
  const [sending, setSending] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [showPoll, setShowPoll] = useState(false); // đang mở hộp tạo bình chọn
  const [pastePrompt, setPastePrompt] = useState(null); // { imgs, text } — dán từ Excel: hỏi gửi ảnh hay chữ
  const [minimized, setMinimized] = useState(false); // thu nhỏ về thanh tiêu đề
  const [minUnread, setMinUnread] = useState(0); // số tin mới nhận khi đang thu nhỏ
  const [showMembers, setShowMembers] = useState(false); // bảng thành viên nhóm
  const [showTasks, setShowTasks] = useState(false); // bảng CÔNG VIỆC (mở được cả mobile)
  const [showAddMember, setShowAddMember] = useState(false); // ô chọn người để thêm
  const [allUsers, setAllUsers] = useState([]); // danh sách nhân viên (để thêm)
  const [addQ, setAddQ] = useState(''); // tìm khi thêm
  const [memberBusy, setMemberBusy] = useState(false);
  const [profileUser, setProfileUser] = useState(null); // thẻ thông tin nhân viên đang xem
  const [typingNames, setTypingNames] = useState([]); // ai đang gõ (hiện "… đang gõ")
  const [reactPickerFor, setReactPickerFor] = useState(null); // id tin đang mở bảng chọn cảm xúc
  const [menuFor, setMenuFor] = useState(null); // id tin đang mở menu ⋯ (chuyển tiếp/ghim/thu hồi...)
  const [editingId, setEditingId] = useState(null); // id tin ĐANG SỬA (composer thành ô sửa)
  // Đóng menu ⋯ khi bấm ra ngoài (không dùng backdrop fixed vì bị kẹt stacking context).
  useEffect(() => {
    if (!menuFor) return;
    const close = (e) => {
      if (lpFired.current) return; // click TỔNG HỢP ngay sau khi GIỮ mở menu -> đừng đóng liền
      if (!e.target.closest('.chatmsg-menu') && !e.target.closest('.chatmsg-more')) setMenuFor(null);
    };
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', close); };
  }, [menuFor]);
  // Bảng CẢM XÚC: đóng khi bấm RA NGOÀI (không đóng khi chỉ di chuột — trước đây onMouseLeave khiến
  // "di chuột là mất", không kịp bấm emoji). Giữ mở tới khi chọn emoji hoặc bấm chỗ khác.
  useEffect(() => {
    if (!reactPickerFor) return;
    const close = (e) => { if (!e.target.closest('.chatmsg-react-picker')) setReactPickerFor(null); };
    const t = setTimeout(() => document.addEventListener('click', close), 0);
    return () => { clearTimeout(t); document.removeEventListener('click', close); };
  }, [reactPickerFor]);
  // GIỮ (long-press) trên mobile để mở menu chức năng (forward/lưu/ghim/thu hồi…). Chạm nhanh = thả cảm xúc.
  const lpTimer = useRef(null);
  const lpFired = useRef(false);
  const startLongPress = useCallback((id) => {
    lpFired.current = false;
    clearTimeout(lpTimer.current);
    lpTimer.current = setTimeout(() => { lpFired.current = true; setReactPickerFor(null); setMenuFor(id); }, 420);
  }, []);
  const cancelLongPress = useCallback(() => clearTimeout(lpTimer.current), []);
  const [peek, setPeek] = useState(null); // popover xem chi tiết: { title, lines:[{icon,name}] }
  // Kích thước cửa sổ do NGƯỜI DÙNG tự chỉnh (lưu localStorage, dùng chung cho mọi cửa sổ chat).
  const [size, setSize] = useState(() => {
    try {
      const s = JSON.parse(localStorage.getItem('ft_chat_size') || 'null');
      if (s && s.w && s.h) return { w: s.w, h: s.h };
    } catch {}
    return null;
  });
  const [readMap, setReadMap] = useState(() => {
    // Thời điểm ĐỌC gần nhất của từng thành viên khác (ms) — để hiện dấu Đã xem.
    const my = String(me.id || me._id);
    const m = {};
    (conv.members || []).forEach((mm) => {
      if (String(mm.id) !== my && mm.readAt) m[String(mm.id)] = new Date(mm.readAt).getTime();
    });
    return m;
  });
  const emojiBtnRef = useRef(null);
  const minimizedRef = useRef(false); // gương của `minimized` cho callback socket (không cần re-subscribe)
  const typingSentRef = useRef(false); // đã gửi "đang gõ" cho đối phương chưa
  const typingStopRef = useRef(null); // timer dừng gõ
  const typingInRef = useRef({}); // name -> timer xoá khi ngừng nhận
  const endRef = useRef(null);
  const msgsRef = useRef(null); // khung cuộn tin nhắn (đo vị trí đang đọc)
  const messagesRef = useRef([]); // gương mảng tin hiện có (để scrollToMsg biết tin cổ nhất mà tải thêm)
  messagesRef.current = messages; // luôn phản ánh mảng tin mới nhất
  const lastMsgIdRef = useRef(null); // id tin cuối đã xử lý cuộn (phân biệt tin MỚI với cập nhật cảm xúc)
  const [newBelow, setNewBelow] = useState(0); // số tin mới đến khi đang đọc tin cũ (hiện mũi tên ↓)
  const [showJump, setShowJump] = useState(false); // hiện nút "Tin chưa đọc" / "Nhắc đến bạn" lúc mới mở (tự ẩn khi cuộn/gõ)
  const [scrolledUp, setScrolledUp] = useState(false); // đang cuộn xa đáy -> luôn cho nút về tin mới nhất
  const panelRef = useRef(null);
  const inputRef = useRef(null);
  const fileRef = useRef(null);
  const unreadRef = useRef(null);
  const initRef = useRef(false);
  const stickBottomRef = useRef(true); // ĐANG "dính đáy"? (mới mở / đang ở gần đáy) -> ảnh/biểu đồ tải xong tự cuộn lại đáy
  const unreadSnapRef = useRef(conv.unread || 0); // số tin chưa đọc lúc mở

  const myId = String(me.id || me._id);
  const isGroup = conv.type === 'group';
  // CHẾ ĐỘ GIÁM SÁT (GD mở nhóm KHÔNG có mình): THUẦN XEM — ẩn ô nhập + mọi nút tương tác,
  // không markRead/"đã xem", không mute. Backend vẫn chặn mọi endpoint ghi (ensureMember).
  const observer = !!conv.observer;
  // TIN GHIM (kiểu Zalo, tối đa 3 — CHUNG cả hội thoại): đọc từ danh sách "sống" trong context
  // để người khác ghim/bỏ ghim là dải ghim tự đổi (refresh); cửa sổ giám sát không nằm trong
  // danh sách -> dùng bản chụp lúc mở.
  const liveConv = convs.find((c) => String(c.id) === String(conv.id)) || conv;
  const tasks = liveConv.tasks || []; // CÔNG VIỆC của hội thoại (đọc bản LIVE để cập nhật khi người khác đổi)
  const taskAssignees = (liveConv.members || conv.members || []).map((m) => ({ id: m.id, name: m.name }));
  const pinnedMsgs = liveConv.pinnedMessages || [];
  const pinnedIds = new Set(pinnedMsgs.map((p) => String(p.msgId)));
  const pinnedKey = pinnedMsgs.map((p) => p.msgId).join(','); // cho memo danh sách tin
  const [pinsOpen, setPinsOpen] = useState(false); // dải ghim đang mở rộng (hiện đủ 3) hay thu gọn (1)
  // Cửa sổ TỰ BUNG do có tin mới -> NHẤP NHÁY báo hiệu (kiểu Facebook) nhưng KHÔNG cướp con trỏ.
  // Tắt nhấp nháy khi người dùng bấm vào cửa sổ này.
  const [blink, setBlink] = useState(!!conv.autoOpened);
  const recipientIds = (conv.members || []).filter((mm) => String(mm.id) !== myId).map((mm) => mm.id);

  // BUZZ! — rung cửa sổ + âm gằn (khác "Nhắc lại": buzz là gọi TỨC THÌ real-time, không đẩy thông báo).
  const [buzzing, setBuzzing] = useState(false);
  const buzzTimerRef = useRef(null);
  const buzzCooldownRef = useRef(0);
  const lastBuzzSigRef = useRef(0);
  const triggerBuzz = useCallback(() => {
    setBuzzing(true);
    clearTimeout(buzzTimerRef.current);
    buzzTimerRef.current = setTimeout(() => setBuzzing(false), 820);
  }, []);
  // Gửi buzz: chống bấm dồn (3s), rung + kêu ngay cho chính mình để có phản hồi tức thì.
  const sendBuzz = useCallback(() => {
    const now = Date.now();
    if (now - buzzCooldownRef.current < 3000) return;
    buzzCooldownRef.current = now;
    emitBuzz(conv.id, recipientIds);
    triggerBuzz();
    playBuzzSound();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id, triggerBuzz]);
  // Nhận tín hiệu buzz (từ ChatContext khi có người buzz đúng hội thoại này) -> rung cửa sổ.
  useEffect(() => {
    if (!buzzSignal || String(buzzSignal.convId) !== String(conv.id)) return;
    if (buzzSignal.at === lastBuzzSigRef.current) return;
    lastBuzzSigRef.current = buzzSignal.at;
    triggerBuzz();
  }, [buzzSignal, conv.id, triggerBuzz]);
  useEffect(() => () => clearTimeout(buzzTimerRef.current), []);

  // Thu nhỏ / mở lại cửa sổ. Khi mở lại: xoá badge + đánh dấu đã đọc.
  const toggleMinimize = useCallback((next) => {
    const on = typeof next === 'boolean' ? next : !minimizedRef.current;
    minimizedRef.current = on;
    setMinimized(on);
    setConvMinimized(conv.id, on);
    if (!on) { setMinUnread(0); if (!conv.observer) import('../api/chat').then(({ markRead }) => markRead(conv.id).catch(() => {})); }
  }, [conv.id, setConvMinimized]);

  // Dọn trạng thái thu nhỏ khỏi context khi đóng cửa sổ.
  useEffect(() => () => setConvMinimized(conv.id, false), [conv.id, setConvMinimized]);
  // Cửa sổ đóng/unmount khi đang focus ô soạn -> xoá cờ "đang gõ" (nếu là chính nó) để lại cho cửa mới bung.
  useEffect(() => () => setComposerFocused(conv.id, false), [conv.id, setComposerFocused]);

  // Thêm thành viên: người TẠO nhóm hoặc Giám đốc/Cố vấn.
  const isCreator = isGroup && String(conv.createdBy || '') === myId;
  const canAddMember = isGroup && (me.role === 'GD' || me.role === 'CV' || isCreator);
  // Xoá thành viên: CHỈ người tạo nhóm.
  const canRemoveMember = isCreator || me.role === 'GD'; // trưởng nhóm hoặc Giám đốc
  // Mở ô "Thêm thành viên": nạp danh sách nhân viên 1 lần.
  const openAddMember = useCallback(() => {
    setShowAddMember((v) => !v);
    if (!allUsers.length) chatUsers().then(setAllUsers).catch(() => {});
  }, [allUsers.length]);
  const addMember = useCallback(async (uid) => {
    if (memberBusy) return;
    setMemberBusy(true);
    try {
      const updated = await addGroupMember(conv.id, uid);
      patchConv(conv.id, { members: updated.members, name: updated.name });
      setAddQ('');
      setShowAddMember(false);
    } catch (e) {
      alert(e?.response?.data?.message || 'Thêm thành viên thất bại');
    } finally { setMemberBusy(false); }
  }, [conv.id, memberBusy, patchConv]);
  const removeMember = useCallback(async (uid, name) => {
    if (memberBusy) return;
    if (!window.confirm(`Xoá ${name} khỏi nhóm?`)) return;
    setMemberBusy(true);
    try {
      const updated = await removeGroupMember(conv.id, uid);
      patchConv(conv.id, { members: updated.members });
    } catch (e) {
      alert(e?.response?.data?.message || 'Xoá thành viên thất bại');
    } finally { setMemberBusy(false); }
  }, [conv.id, memberBusy, patchConv]);
  // Đổi tên nhóm: MỌI thành viên đều được đổi.
  const renameGroupChat = useCallback(async () => {
    if (memberBusy) return;
    const next = window.prompt('Đổi tên nhóm:', conv.name || '');
    if (next == null) return;
    const name = next.trim();
    if (!name || name === conv.name) return;
    setMemberBusy(true);
    try {
      const updated = await renameGroup(conv.id, name);
      patchConv(conv.id, { name: updated.name });
    } catch (e) {
      alert(e?.response?.data?.message || 'Đổi tên nhóm thất bại');
    } finally { setMemberBusy(false); }
  }, [conv.id, conv.name, memberBusy, patchConv]);

  // XOÁ NHÓM (chỉ người tạo nhóm): xoá cả nhóm + tin nhắn, gỡ khỏi danh sách của mọi người.
  const deleteGroupChatHandler = useCallback(async () => {
    if (memberBusy) return;
    if (!window.confirm(`Giải tán nhóm "${conv.name}"?\n\nToàn bộ tin nhắn của nhóm sẽ bị xoá và không khôi phục được. Nhóm biến mất khỏi danh sách của tất cả thành viên.`)) return;
    setMemberBusy(true);
    try {
      await deleteGroupChat(conv.id);
      removeConvsLocal([conv.id]);
      closeWindow(conv.id);
    } catch (e) {
      alert(e?.response?.data?.message || 'Xoá nhóm thất bại');
    } finally { setMemberBusy(false); }
  }, [conv.id, conv.name, memberBusy, removeConvsLocal, closeWindow]);

  // Bấm tên/avatar 1 người -> thẻ thông tin (phòng ban + chức vụ). Nạp danh sách NV để lấy phòng ban.
  const openProfile = useCallback(async (member) => {
    if (!member?.id) return;
    let users = allUsers;
    if (!users.length) { try { users = await chatUsers(); setAllUsers(users); } catch { users = []; } }
    const u = users.find((x) => String(x.id) === String(member.id)) || {};
    setProfileUser({ id: member.id, name: member.name || u.name || '', role: member.role || u.role || '', departments: u.departments || [], avatar: member.avatar || u.avatar || '', resigned: !!member.resigned });
  }, [allUsers]);
  // Nhắn tin riêng với người đang xem thẻ.
  const chatWithProfile = useCallback(() => {
    const p = profileUser;
    if (!p || String(p.id) === myId) return;
    setProfileUser(null);
    startDirect(p.id).catch(() => {});
  }, [profileUser, myId, startDirect]);

  // Báo "đang gõ" cho đối phương: gửi bật 1 lần, tự tắt sau 2,5s ngừng gõ.
  const notifyTyping = useCallback((on) => {
    if (on) {
      if (!typingSentRef.current) { typingSentRef.current = true; emitTyping(conv.id, recipientIds, true); }
      clearTimeout(typingStopRef.current);
      typingStopRef.current = setTimeout(() => { typingSentRef.current = false; emitTyping(conv.id, recipientIds, false); }, 2500);
    } else {
      clearTimeout(typingStopRef.current);
      if (typingSentRef.current) { typingSentRef.current = false; emitTyping(conv.id, recipientIds, false); }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  // Dừng báo gõ + dọn timer khi đóng cửa sổ / đổi hội thoại.
  useEffect(() => () => { notifyTyping(false); Object.values(typingInRef.current).forEach(clearTimeout); }, [notifyTyping]);

  // Tải tin nhắn khi mở cửa sổ (40 tin mới nhất; tin cũ hơn tải tiếp bằng nút "Xem tin cũ hơn").
  const [hasOlder, setHasOlder] = useState(false); // còn tin cũ hơn chưa tải không
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [hasNewer, setHasNewer] = useState(false); // đã nhảy tới tin cũ -> còn tin MỚI HƠN cửa sổ để cuộn xuống đọc tiếp
  const [loadingNewer, setLoadingNewer] = useState(false);
  const restoreOlderRef = useRef(null); // khoảng cách-tới-đáy cần khôi phục sau khi chèn tin cũ lên đầu
  useEffect(() => {
    let alive = true;
    setLoadingMsgs(true);
    initRef.current = false;
    jumpedRef.current = false; // mở/đổi hội thoại -> về chế độ xem tin mới nhất
    setHasNewer(false);        // mở hội thoại = xem tin mới nhất -> không còn tin mới hơn để nạp xuôi
    unreadSnapRef.current = conv.unread || 0;
    setShowJump((conv.unread || 0) > 0); // có tin chưa đọc -> hiện nút nhảy (Zalo-style); tự ẩn khi cuộn/gõ
    listMessages(conv.id, { limit: 40 })
      .then((msgs) => { if (alive) { setMessages(msgs); setHasOlder(msgs.length >= 40); } })
      .finally(() => alive && setLoadingMsgs(false));
    return () => {
      alive = false;
    };
  }, [conv.id]);

  // Nút nhảy TỰ ẨN khi người dùng GÕ tin mới (bắt đầu soạn) — theo yêu cầu (không bấm thì mất).
  useEffect(() => { if (text) setShowJump(false); }, [text]);
  // ... hoặc khi NGƯỜI DÙNG cuộn khung (dùng wheel/touchmove = cử chỉ THẬT, không dính auto-scroll lúc mở).
  useEffect(() => {
    const el = msgsRef.current;
    if (!el) return;
    const off = () => setShowJump(false);
    el.addEventListener('wheel', off, { passive: true });
    el.addEventListener('touchmove', off, { passive: true });
    return () => { el.removeEventListener('wheel', off); el.removeEventListener('touchmove', off); };
  }, [conv.id]);

  // XEM TIN CŨ HƠN: tải thêm 40 tin TRƯỚC tin cổ nhất đang có, chèn lên đầu và GIỮ NGUYÊN
  // vị trí đang đọc (không giật về cuối). Sửa lỗi "mất tin cũ trước 17/7": tin vẫn nằm nguyên
  // trên máy chủ, chỉ là trước đây không có đường nào xem quá 40 tin mới nhất.
  // reveal=true (BẤM NÚT "Xem tin cũ hơn"): sau khi nạp phải CUỘN LÊN để HIỆN các tin cũ vừa tải ra màn hình
  // (người dùng chủ động muốn xem tin cũ). reveal=false (CUỘN TAY chạm gần đỉnh): GIỮ NGUYÊN chỗ đọc để cuộn
  // vô tận mượt (tin cũ chèn lên trên, tự nhiên lộ ra khi cuộn tiếp) — không giật.
  const loadOlder = useCallback(async (reveal = false) => {
    if (loadingOlder) return;
    // Mốc "tin cổ nhất" để tải trước nó: tin ĐÃ XÁC NHẬN từ server (không phải tin TẠM đang gửi/lỗi). LƯU Ý:
    // server GIỜ lưu & trả kèm clientId (chống trùng) -> KHÔNG được coi "có clientId = tin tạm" (sẽ bỏ qua
    // MỌI tin -> oldest=null -> loadOlder thoát ngay -> "bấm không xem được tin cũ"). Nhận diện tin tạm bằng
    // pending/failed. Lấy createdAt NHỎ NHẤT (phòng mảng lệch thứ tự).
    const oldest = messages.reduce(
      (min, m) => (m.createdAt && !m.pending && !m.failed && (!min || new Date(m.createdAt) < new Date(min.createdAt)) ? m : min),
      null,
    );
    if (!oldest) return;
    setLoadingOlder(true);
    try {
      const older = await listMessages(conv.id, { before: oldest.createdAt, limit: 40 });
      const el = msgsRef.current;
      // Chốt số đo TRƯỚC khi chèn; điều chỉnh scrollTop ở useLayoutEffect (SAU khi React commit tin mới vào
      // DOM, scrollHeight đã đúng). Trước dùng setTimeout(0) -> chạy TRƯỚC lúc DOM cập nhật -> đọc scrollHeight
      // CŨ -> sai vị trí.
      restoreOlderRef.current = el ? { keep: el.scrollHeight - el.scrollTop, oldSH: el.scrollHeight, reveal } : null;
      setMessages((prev) => {
        const have = new Set(prev.map((m) => String(m._id)));
        return [...older.filter((m) => !have.has(String(m._id))), ...prev];
      });
      setHasOlder(older.length >= 40);
    } catch { /* mạng lỗi -> bấm lại */ }
    finally { setLoadingOlder(false); }
  }, [conv.id, messages, loadingOlder]);

  // Điều chỉnh scrollTop SAU khi chèn tin cũ (DOM đã có tin mới -> số đo đúng -> không giật).
  useLayoutEffect(() => {
    const info = restoreOlderRef.current;
    if (info == null) return;
    const el = msgsRef.current;
    if (el) {
      if (info.reveal) {
        // BẤM NÚT: lộ các tin cũ vừa nạp, giữ tin đang đọc ở mép dưới cho liền mạch (cuộn tiếp lên để xem cũ hơn).
        const prepended = el.scrollHeight - info.oldSH;
        el.scrollTop = Math.max(0, prepended - el.clientHeight + 60);
      } else {
        // CUỘN TAY: giữ NGUYÊN chỗ đang đọc.
        el.scrollTop = el.scrollHeight - info.keep;
      }
    }
    restoreOlderRef.current = null;
  }, [messages]);

  // Nhận tin mới realtime cho riêng hội thoại này.
  useEffect(() => {
    const unsub = subscribeMessages(conv.id, (payload) => {
      // Nhận tin của người khác khi đang thu nhỏ -> đếm badge chưa đọc trên thanh tiêu đề. Nhận diện "của mình"
      // CHỈ bằng sender (KHÔNG bằng payload.clientId — server trả clientId cho cả tin người khác -> trước đây
      // nuốt badge + coi nhầm tin người khác là của mình).
      const mineNow = String(payload.sender?._id) === String(me.id || me._id);
      // Tin hệ thống (ai vào/rời nhóm) không tính vào badge chưa đọc — chỉ hiện dòng thông báo trong khung.
      if (minimizedRef.current && !mineNow && !payload.system) {
        setMinUnread((n) => n + 1);
      }
      // Đang xem lịch sử (đã nhảy tới tin cũ) VÀ đang cuộn XA đáy -> chỉ báo "có tin mới", KHÔNG ghép
      // (tránh lệch cửa sổ). Nếu đang Ở ĐÁY (hoặc tin của mình) -> coi như xem trực tiếp: THOÁT chế độ
      // nhảy + ghép bình thường. (Trước đây jumpedRef hay kẹt true sau reconnect/poll -> tin mới bị
      // nuốt dù đang ở đáy, phải F5 mới thấy — dù danh sách đã hiện tin.)
      if (jumpedRef.current && !mineNow && !isNearBottom()) { setNewBelow((n) => n + 1); return; }
      if (jumpedRef.current) jumpedRef.current = false;
      setMessages((prev) => {
        // Khớp tin tạm (gửi tức thì) theo clientId -> thay bằng bản thật từ server.
        if (payload.clientId && prev.some((m) => m.clientId && m.clientId === payload.clientId)) {
          return prev.map((m) => (m.clientId === payload.clientId ? { ...payload } : m));
        }
        return prev.some((m) => String(m._id) === String(payload._id)) ? prev : [...prev, payload];
      });
    });
    return unsub;
  }, [conv.id, subscribeMessages]); // eslint-disable-line react-hooks/exhaustive-deps

  // DỰ PHÒNG khi socket chập chờn (VD web thật sau proxy không giữ được WebSocket): tải lại tin mới
  // nhất và gộp vào -> tin của người khác vẫn hiện mà KHỎI PHẢI F5. Giữ nguyên tin đang gửi/lỗi.
  // QUAN TRỌNG: KHÔNG chỉ dựa vào socket.connected. Web sau proxy thường "connected" nhưng vẫn LỠ
  // event (danh sách hội thoại refresh 20s/lần nên vẫn hiện tin, còn khung thì kẹt tới khi F5). Vì vậy
  // khi socket "khoẻ" vẫn tải bù ĐỊNH KỲ ~10s (tick chẵn); socket rớt thì 5s/lần. mergeLatest chống
  // nhân đôi + trả về NGUYÊN mảng cũ khi không có tin mới -> không re-render, không nhấp nháy.
  useEffect(() => {
    const tick = () => {
      if (document.hidden) return;
      if (jumpedRef.current) return; // đang xem cửa sổ lịch sử -> KHÔNG poll (tránh ghép tin mới làm vỡ cửa sổ)
      listMessages(conv.id, { limit: 40 })
        .then((msgs) => setMessages((prev) => mergeLatest(prev, msgs)))
        .catch(() => {});
    };
    const t = setInterval(tick, 5000); // tải bù mỗi 5s (kể cả khi socket "khoẻ" nhưng lỡ event sau proxy)
    return () => clearInterval(t);
  }, [conv.id]);

  // KHUNG KHÔNG BAO GIỜ CHẬM HƠN DANH SÁCH: danh sách hội thoại (convs) cập nhật tin mới nhất qua
  // socket/refresh; nếu tin cuối của khung CŨ HƠN tin cuối danh sách -> tải bù NGAY (không đợi poll
  // 10s). Bình thường subscriber đã ghép rồi nên guard bên dưới bỏ qua, không gọi thừa.
  const listLastAt = convs.find((c) => String(c.id) === String(conv.id))?.lastMessage?.at;
  useEffect(() => {
    if (!listLastAt || jumpedRef.current) return;
    const haveLast = messagesRef.current[messagesRef.current.length - 1];
    const haveAt = haveLast && haveLast.createdAt ? new Date(haveLast.createdAt).getTime() : 0;
    if (haveAt >= new Date(listLastAt).getTime()) return; // khung đã có tin mới nhất
    listMessages(conv.id, { limit: 40 })
      .then((msgs) => setMessages((prev) => mergeLatest(prev, msgs)))
      .catch(() => {});
  }, [listLastAt, conv.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nhận "đã xem" từ đối phương -> cập nhật readMap để render dấu Đã xem.
  useEffect(() => {
    const unsub = subscribeRead(conv.id, ({ user, at }) => {
      setReadMap((prev) => ({ ...prev, [String(user)]: new Date(at).getTime() }));
    });
    return unsub;
  }, [conv.id, subscribeRead]);

  // Nhận "đang gõ" từ người khác trong hội thoại này.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onTyping = (evt) => {
      if (String(evt.conversation) !== String(conv.id)) return;
      if (String(evt.from) === String(me.id || me._id)) return;
      const nm = evt.name || 'Ai đó';
      if (evt.typing) {
        setTypingNames((prev) => (prev.includes(nm) ? prev : [...prev, nm]));
        clearTimeout(typingInRef.current[nm]);
        typingInRef.current[nm] = setTimeout(() => {
          setTypingNames((prev) => prev.filter((x) => x !== nm));
          delete typingInRef.current[nm];
        }, 4000);
      } else {
        clearTimeout(typingInRef.current[nm]);
        delete typingInRef.current[nm];
        setTypingNames((prev) => prev.filter((x) => x !== nm));
      }
    };
    socket.on('chat:typing', onTyping);
    return () => socket.off('chat:typing', onTyping);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conv.id]);

  // Khi socket (re)kết nối lại (sau khi backend chập chờn) -> nạp lại tin để không phải F5.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    // Tái kết nối -> đồng bộ tin mới nhất nhưng GỘP (giữ tin cũ đang đọc + tin đang gửi), KHÔNG thay cả list.
    const onReconnect = () =>
      listMessages(conv.id, { limit: 40 })
        .then((msgs) => setMessages((prev) => mergeLatest(prev, msgs)))
        .catch(() => {});
    socket.on('connect', onReconnect);
    return () => socket.off('connect', onReconnect);
  }, [conv.id]);

  // TAB HIỆN LẠI / SOCKET NỐI LẠI (wakeSignal bump) -> tải bù tin NGAY, không đợi tick 5s. Bịt "thông
  // báo đến mà nội dung chưa hiện": bấm thông báo quay lại là thấy tin tức thì.
  useEffect(() => {
    if (!wakeSignal || jumpedRef.current) return;
    listMessages(conv.id, { limit: 40 })
      .then((msgs) => setMessages((prev) => mergeLatest(prev, msgs)))
      .catch(() => {});
  }, [wakeSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // Nhận cảm xúc (react) realtime -> cập nhật reactions của đúng tin.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onReaction = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((m) => (String(m._id) === String(p.messageId) ? { ...m, reactions: p.reactions } : m)));
    };
    socket.on('chat:reaction', onReaction);
    return () => socket.off('chat:reaction', onReaction);
  }, [conv.id]);

  // Nhận cập nhật BÌNH CHỌN realtime -> đồng bộ phiếu/đóng của đúng tin.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onPoll = (p) => {
      if (String(p.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((m) => (String(m._id) === String(p.messageId) && m.poll ? { ...m, poll: { ...m.poll, votes: p.votes, closed: p.closed } } : m)));
    };
    socket.on('chat:poll', onPoll);
    return () => socket.off('chat:poll', onPoll);
  }, [conv.id]);

  // Đang ở gần đáy khung tin nhắn? (đọc tin cũ = cuộn lên xa đáy)
  const isNearBottom = () => {
    const el = msgsRef.current;
    if (!el) return true;
    return el.scrollHeight - el.scrollTop - el.clientHeight < 120;
  };
  const jumpToLatest = async () => {
    // Đang ở CỬA SỔ LỊCH SỬ (đã nhảy tới 1 tin cũ) -> tin mới nhất chưa nạp: tải lại 40 tin mới nhất
    // để quay về hôm nay (kiểu Facebook), rồi mới cuộn xuống đáy.
    if (jumpedRef.current) {
      try {
        const fresh = await listMessages(conv.id, { limit: 40 });
        setMessages(fresh);
        setHasOlder(fresh.length >= 40);
        jumpedRef.current = false;
      } catch { /* mạng lỗi -> bấm lại */ }
    }
    // Gán scrollTop trực tiếp thay vì scrollIntoView({smooth}): smooth có môi trường bị vô hiệu
    // (đã gặp khi kiểm thử) -> bấm mũi tên mà không nhảy. Quãng cuộn dài, nhảy thẳng còn rõ hơn.
    stickBottomRef.current = true; // muốn ở đáy -> ảnh tải xong sau đó vẫn giữ ở đáy
    requestAnimationFrame(() => { const el = msgsRef.current; if (el) el.scrollTop = el.scrollHeight; });
    setNewBelow(0);
    setScrolledUp(false);
  };

  // Lần đầu mở: cuộn tới ngay đầu các tin chưa đọc (nếu có), ngược lại xuống cuối.
  // Tin MỚI đến sau đó: chỉ tự cuộn khi (a) tin của MÌNH vừa gửi, hoặc (b) đang ở gần đáy.
  // Đang kéo lên đọc tin cũ -> GIỮ NGUYÊN vị trí, hiện mũi tên "↓ tin mới" để bấm xuống.
  useEffect(() => {
    if (!messages.length) return;
    const last = messages[messages.length - 1];
    const lastId = String(last.clientId || last._id);
    if (!initRef.current) {
      initRef.current = true;
      lastMsgIdRef.current = lastId;
      // LUÔN nhảy xuống TIN MỚI NHẤT khi mở hội thoại (không dừng ở tin chưa đọc cũ tít trên). Cuộn lại vài
      // nhịp vì ảnh/nội dung giãn ra + content-visibility ước lượng chiều cao làm lệch đáy lúc đầu.
      jumpToLatest();
      setTimeout(jumpToLatest, 60);
      setTimeout(jumpToLatest, 300);
      return;
    }
    if (lastMsgIdRef.current === lastId) return; // chỉ cảm xúc/đã xem đổi -> không cuộn
    lastMsgIdRef.current = lastId;
    if (String(last.sender?._id) === myId || isNearBottom()) {
      // TỨC THÌ (không 'smooth'): tin mới hiện NGAY như badge trên danh sách, không trượt chậm ~400ms.
      endRef.current?.scrollIntoView();
    } else {
      setNewBelow((n) => n + 1);
    }
  }, [messages]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (atts.length) endRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [atts]); // eslint-disable-line react-hooks/exhaustive-deps

  // ẢNH/BIỂU ĐỒ trong tin tải xong -> KHUNG CAO LÊN, đẩy nội dung xuống. Nếu đang DÍNH ĐÁY thì kéo lại đáy
  // (không bị trôi lên). Bắt 'load' của <img> bằng capture vì 'load' KHÔNG bubble. Sửa lỗi "mở chat nhiều
  // ảnh bị cuộn lên, phải bấm 'Tin mới nhất' nhiều lần mới xuống".
  useEffect(() => {
    const el = msgsRef.current;
    if (!el) return;
    const onLoadCapture = () => { if (stickBottomRef.current) el.scrollTop = el.scrollHeight; };
    el.addEventListener('load', onLoadCapture, true);
    return () => el.removeEventListener('load', onLoadCapture, true);
  }, [conv.id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ô nhập tự cao theo nội dung đang gõ, TỐI ĐA ~5 dòng (112px, khớp max-height CSS) rồi cuộn
  // trong ô. Đặt 'auto' trước khi đo, nếu không scrollHeight giữ chiều cao cũ khi xoá bớt chữ.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 112) + 'px';
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  // Lưu/nhớ bản nháp theo hội thoại (chuyển nhóm không mất đoạn đang gõ) + hiện "Chưa gửi" ở danh sách.
  useEffect(() => { setDraft(conv.id, text); }, [text, conv.id, setDraft]);

  function addFiles(fileList) {
    const items = [...fileList].map((f) => ({
      file: f,
      name: f.name,
      type: f.type,
      size: f.size,
      preview: f.type.startsWith('image/') ? URL.createObjectURL(f) : null,
    }));
    setAtts((prev) => [...prev, ...items]);
  }
  function onPaste(e) {
    const imgs = [...(e.clipboardData?.items || [])].filter((it) => it.kind === 'file').map((it) => it.getAsFile()).filter(Boolean);
    const txt = e.clipboardData?.getData('text/plain') || '';
    // DÁN TỪ EXCEL/BẢNG: clipboard có CẢ ảnh CẢ văn bản -> hỏi gửi dạng nào (giống Zalo).
    if (imgs.length && txt.trim()) {
      e.preventDefault();
      setPastePrompt({ imgs, text: txt });
      return;
    }
    // Chỉ có ảnh (chụp màn hình) -> đính kèm luôn. Chỉ có chữ -> để dán mặc định.
    if (imgs.length) {
      e.preventDefault();
      addFiles(imgs);
    }
  }
  // Người dùng chọn dạng gửi cho nội dung vừa dán (Excel).
  const choosePasteImage = () => { if (pastePrompt) addFiles(pastePrompt.imgs); setPastePrompt(null); };
  const choosePasteText = () => { const p = pastePrompt; setPastePrompt(null); if (p) insertEmoji(p.text); };
  function onDrop(e) {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  }

  // Lựa chọn để tag: @Tất cả + các thành viên khác.
  const mentionOptions = isGroup
    ? [{ id: 'all', name: MENTION_ALL }, ...(conv.members || []).filter((mm) => String(mm.id) !== myId)]
    : [];
  const mentionNames = mentionOptions.map((o) => o.name);
  const mentionKey = mentionNames.join('|');
  const suggestions =
    men && isGroup ? mentionOptions.filter((o) => o.name.toLowerCase().includes((men.q || '').toLowerCase())) : [];

  function onChange(e) {
    const val = e.target.value;
    setText(val);
    notifyTyping(val.trim().length > 0); // báo/tắt "đang gõ"
    if (!isGroup) return;
    const caret = e.target.selectionStart;
    const upto = val.slice(0, caret);
    const at = upto.lastIndexOf('@');
    if (at >= 0 && (at === 0 || /\s/.test(upto[at - 1]))) setMen({ at, q: upto.slice(at + 1) });
    else setMen(null);
  }

  // Click ra ngoài bảng sticker/emoji (không phải nút mở) -> tự đóng bảng.
  useEffect(() => {
    if (!showEmoji) return;
    const onDown = (e) => {
      if (e.target.closest?.('.emoji-pop')) return; // click trong bảng
      if (emojiBtnRef.current?.contains(e.target)) return; // click nút mở (đã tự toggle)
      setShowEmoji(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [showEmoji]);

  // Đổi nội dung ô soạn BẰNG CODE (uncontrolled -> phải tự ghi DOM rồi đồng bộ state).
  const putText = useCallback((v) => {
    const el = inputRef.current;
    if (el) el.value = v;
    setText(v);
  }, []);

  // Chèn emoji vào ô nhập tại vị trí con trỏ (ổn định).
  const insertEmoji = useCallback((emo) => {
    const el = inputRef.current;
    const cur = el ? el.value : text;
    const a = el ? el.selectionStart : cur.length;
    const b = el ? el.selectionEnd : cur.length;
    const next = cur.slice(0, a) + emo + cur.slice(b);
    putText(next);
    setTimeout(() => {
      el?.focus();
      const p = a + emo.length;
      el?.setSelectionRange(p, p);
    }, 0);
  }, [text, putText]);

  // Gửi sticker = gửi 1 tin chỉ mang ID sticker (client tra bộ sticker để vẽ thẻ cỡ lớn).
  // Hiện NGAY (lạc quan) rồi thay bằng bản thật khi server trả về — khỏi khựng 1 vòng mạng.
  const sendSticker = useCallback(
    async (stickerId) => {
      setShowEmoji(false);
      const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const temp = { _id: clientId, clientId, sender: { _id: myId, name: me.name }, text: '', sticker: stickerId, attachments: [], createdAt: new Date().toISOString(), pending: true };
      setMessages((prev) => [...prev, temp]);
      try {
        const msg = await sendMessage(conv.id, '', { sticker: stickerId, clientId });
        setMessages((prev) => {
          if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) return prev.filter((m) => m.clientId !== clientId);
          return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
        });
        bumpConv(conv.id, msg);
      } catch {
        setMessages((prev) => prev.map((m) => (m.clientId === clientId && m.pending ? { ...m, pending: false, failed: true } : m)));
      }
    },
    [conv.id, bumpConv, myId, me.name]
  );

  // Gửi 1 BÌNH CHỌN (chỉ nhóm).
  const sendPoll = useCallback(
    async (pollData) => {
      setShowPoll(false);
      try {
        const msg = await sendMessage(conv.id, '', { poll: pollData });
        setMessages((prev) => (prev.some((m) => String(m._id) === String(msg._id)) ? prev : [...prev, msg]));
        bumpConv(conv.id, msg);
      } catch (err) {
        alert(err?.response?.data?.message || err?.message || 'Tạo bình chọn thất bại');
      }
    },
    [conv.id, bumpConv]
  );

  // Bình chọn / bỏ chọn 1 phương án — cập nhật lạc quan, socket 'chat:poll' sẽ đồng bộ bản chuẩn.
  const doVote = useCallback(
    async (msg, option) => {
      try {
        const out = await votePoll(conv.id, msg._id, option);
        setMessages((prev) => prev.map((m) => (String(m._id) === String(msg._id) && m.poll ? { ...m, poll: { ...m.poll, votes: out.votes, closed: out.closed } } : m)));
      } catch (err) {
        alert(err?.response?.data?.message || err?.message || 'Bình chọn thất bại');
      }
    },
    [conv.id]
  );

  const doClosePoll = useCallback(
    async (msg) => {
      try {
        const out = await closePoll(conv.id, msg._id);
        setMessages((prev) => prev.map((m) => (String(m._id) === String(msg._id) && m.poll ? { ...m, poll: { ...m.poll, votes: out.votes, closed: true } } : m)));
      } catch (err) {
        alert(err?.response?.data?.message || err?.message || 'Không đóng được bình chọn');
      }
    },
    [conv.id]
  );

  function pickMention(o) {
    const end = men.at + 1 + (men.q || '').length;
    const next = text.slice(0, men.at) + '@' + o.name + ' ' + text.slice(end);
    putText(next);
    setMen(null);
    setTimeout(() => inputRef.current?.focus(), 0);
  }

  const saveNote = useCallback(
    async (m) => {
      const content = (m.text || '').trim() || (m.attachments || []).map((a) => a.name).join(', ') || '[Đính kèm]';
      try {
        await createNote({ content, source: 'chat', fromName: m.sender?.name || '' });
        toast('Đã lưu vào ghi chú cá nhân');
      } catch {
        toast('Không lưu được ghi chú', 'danger');
      }
    },
    [toast]
  );

  // Tin chưa đọc đầu tiên = tin ĐẾN thứ N tính từ cuối (N = số chưa đọc lúc mở).
  const firstUnreadId = (() => {
    if (observer) return null; // giám sát: không có "chưa đọc"
    const n = unreadSnapRef.current;
    if (!n) return null;
    const incoming = messages.filter((m) => String(m.sender?._id) !== myId);
    if (!incoming.length) return null;
    return incoming[Math.max(0, incoming.length - n)]?._id || null;
  })();
  // Tin TAG MÌNH cũ nhất trong số CHƯA ĐỌC (mentionAll hoặc mentions có id mình) -> nút "Nhắc đến bạn".
  const firstUnreadMentionId = (() => {
    if (observer) return null;
    const n = unreadSnapRef.current;
    if (!n) return null;
    const incoming = messages.filter((m) => String(m.sender?._id) !== myId);
    if (!incoming.length) return null;
    const unread = incoming.slice(Math.max(0, incoming.length - n));
    const hit = unread.find((m) => m.mentionAll || (m.mentions || []).some((x) => String(x?._id || x) === myId));
    return hit?._id || null;
  })();

  async function submit(e) {
    e?.preventDefault();
    const t = (inputRef.current?.value ?? text).trim(); // đọc trực tiếp DOM (uncontrolled) cho chắc
    if ((!t && !atts.length) || sending) return;
    // ĐANG SỬA 1 tin -> gọi API sửa, cập nhật tại chỗ, thoát chế độ sửa.
    if (editingId) {
      if (!t) return;
      const id = editingId;
      setEditingId(null); putText(''); notifyTyping(false);
      try {
        await editMessage(conv.id, id, t);
        setMessages((prev) => prev.map((m) => (String(m._id) === String(id) ? { ...m, text: t, edited: true, editedAt: new Date().toISOString() } : m)));
      } catch (err) {
        alert(err?.response?.data?.message || 'Sửa tin thất bại');
        putText(t); setEditingId(id);
      }
      return;
    }
    const mentionAll = isGroup && t.includes('@' + MENTION_ALL);
    let mentions = isGroup
      ? (conv.members || []).filter((mm) => String(mm.id) !== myId && t.includes('@' + mm.name)).map((mm) => mm.id)
      : [];
    const reply = replyTo; // tin đang trả lời (nếu có)
    // Trả lời tin người khác trong nhóm -> TỰ TAG (mention) người đó để họ được báo.
    if (reply?.senderId && isGroup && String(reply.senderId) !== myId && !mentions.includes(reply.senderId)) mentions = [...mentions, reply.senderId];
    const replySnap = reply ? { message: reply.id, senderName: reply.senderName, text: reply.text } : null;
    const sentAtts = atts;
    putText('');
    setMen(null);
    setAtts([]);
    setReplyTo(null);
    notifyTyping(false); // gửi = ngừng gõ

    // Đường nhanh: tin CHỈ có chữ -> hiện ngay (optimistic), gửi ngầm, thay bằng bản thật khi xong.
    if (!sentAtts.length) {
      const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
      const temp = { _id: clientId, clientId, sender: { _id: myId, name: me.name }, text: t, attachments: [], mentions, mentionAll, replyTo: replySnap, createdAt: new Date().toISOString(), pending: true };
      setMessages((prev) => [...prev, temp]);
      try {
        const msg = await sendMessage(conv.id, t, { mentions, mentionAll, clientId, replyTo: reply?.id });
        setMessages((prev) => {
          // Bản thật có thể đã tới qua socket (khớp clientId); dù cách nào cũng chỉ còn 1 tin.
          if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) {
            return prev.filter((m) => m.clientId !== clientId);
          }
          return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
        });
        bumpConv(conv.id, msg);
      } catch {
        // Đánh dấu lỗi để hiện "Gửi lại".
        setMessages((prev) => prev.map((m) => (m.clientId === clientId && m.pending ? { ...m, pending: false, failed: true } : m)));
      }
      return;
    }

    // Có đính kèm: hiện NGAY (optimistic) bằng preview cục bộ, upload ngầm + %tiến trình, thay bằng bản thật.
    const clientId = 'tmp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8);
    const tempAtts = sentAtts.map((a) => ({ url: a.preview || '', name: a.file.name, type: a.file.type, size: a.file.size }));
    const temp = { _id: clientId, clientId, sender: { _id: myId, name: me.name }, text: t, attachments: tempAtts, mentions, mentionAll, replyTo: replySnap, createdAt: new Date().toISOString(), pending: true, progress: 0 };
    setMessages((prev) => [...prev, temp]);
    try {
      const metas = await uploadChatFiles(sentAtts.map((a) => a.file), (pct) =>
        setMessages((prev) => prev.map((m) => (m.clientId === clientId ? { ...m, progress: pct } : m))));
      const msg = await sendMessage(conv.id, t, { mentions, mentionAll, attachments: metas, clientId, replyTo: reply?.id });
      setMessages((prev) => {
        if (prev.some((m) => String(m._id) === String(msg._id) && m.clientId !== clientId)) return prev.filter((m) => m.clientId !== clientId);
        return prev.map((m) => (m.clientId === clientId ? { ...msg } : m));
      });
      bumpConv(conv.id, msg);
      sentAtts.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
    } catch {
      // Giữ bong bóng, đánh dấu lỗi + lưu file để "Gửi lại".
      setMessages((prev) => prev.map((m) => (m.clientId === clientId && m.pending ? { ...m, pending: false, failed: true, _retryFiles: sentAtts } : m)));
    }
  }

  // Gửi lại 1 tin bị lỗi (chữ hoặc có đính kèm — tải lại file nếu có).
  const resend = useCallback(async (failed) => {
    setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, pending: true, failed: false, progress: 0 } : m)));
    try {
      let msg;
      if (failed._retryFiles && failed._retryFiles.length) {
        const metas = await uploadChatFiles(failed._retryFiles.map((a) => a.file), (pct) =>
          setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...m, progress: pct } : m))));
        msg = await sendMessage(conv.id, failed.text || '', { mentions: failed.mentions, mentionAll: failed.mentionAll, attachments: metas, clientId: failed.clientId });
        failed._retryFiles.forEach((a) => a.preview && URL.revokeObjectURL(a.preview));
      } else {
        msg = await sendMessage(conv.id, failed.text, { mentions: failed.mentions, mentionAll: failed.mentionAll, clientId: failed.clientId });
      }
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId ? { ...msg } : m)));
      bumpConv(conv.id, msg);
    } catch {
      setMessages((prev) => prev.map((m) => (m.clientId === failed.clientId && m.pending ? { ...m, pending: false, failed: true } : m)));
    }
  }, [conv.id, bumpConv]);

  // Bắt đầu trả lời 1 tin (reply kiểu Zalo): lưu tin gốc + focus ô nhập.
  // TẮT/BẬT âm hội thoại (riêng mình): tắt thì không kêu, không popup, không push.
  const muted = !!conv.muted;
  const doMute = useCallback(async () => {
    try {
      const r = await muteConversation(conv.id, !muted);
      patchConv(conv.id, { muted: r.muted });
      toast(r.muted ? '🔕 Đã tắt âm hội thoại này' : '🔔 Đã bật lại thông báo');
    } catch (e) { toast(e?.response?.data?.message || 'Không đổi được', 'danger'); }
  }, [conv.id, muted, patchConv, toast]);

  // Nhắc lại thông báo cho người được TAG (nhóm) / người kia (chat riêng) khi chưa thấy phản hồi.
  const doRemind = useCallback(async (m) => {
    try {
      const r = await remindMessage(conv.id, m._id);
      const who = (r.names || []).join(', ');
      toast(`🔔 Đã nhắc lại${who ? ` cho ${who}` : ''}${r.sent < r.targets ? ' (có người đang tắt âm)' : ''}`);
    } catch (e) { toast(e?.response?.data?.message || 'Nhắc lại thất bại', 'danger'); }
  }, [conv.id, toast]);

  // Thu hồi tin của mình (trong 10'): server xoá nội dung, mọi người thấy "đã thu hồi".
  const doRecall = useCallback(async (m) => {
    if (!window.confirm('Thu hồi tin nhắn này? Mọi người sẽ thấy "Tin nhắn đã được thu hồi".')) return;
    try {
      await recallMessage(conv.id, m._id);
      setMessages((prev) => prev.map((x) => (String(x._id) === String(m._id) ? { ...x, recalled: true, text: '', attachments: [] } : x)));
    } catch (e) { toast(e?.response?.data?.message || 'Thu hồi thất bại', 'danger'); }
  }, [conv.id, toast]);

  // Bắt đầu SỬA 1 tin của mình: đưa nội dung vào ô soạn (gửi = lưu sửa), thoát reply/menu.
  const startEdit = useCallback((m) => {
    setEditingId(m._id);
    putText(m.text || '');
    setReplyTo(null);
    setMenuFor(null);
  }, [putText]);

  // SAO CHÉP nội dung tin — cần trên MOBILE: long-press mở menu app nên không "chọn chữ + copy" như web.
  // navigator.clipboard cần ngữ cảnh bảo mật (app/https có); WebView cũ thì fallback execCommand.
  const doCopy = useCallback(async (m) => {
    const t = (m.text || '').trim();
    if (!t) { toast('Tin này không có chữ để sao chép', 'danger'); return; }
    // Fallback execCommand cho WebView cũ HOẶC khi Clipboard API bị chặn (thiếu user-activation).
    const legacy = () => {
      try {
        const ta = document.createElement('textarea');
        ta.value = t; ta.setAttribute('readonly', ''); ta.style.position = 'fixed'; ta.style.top = '0'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.focus(); ta.select(); ta.setSelectionRange(0, t.length);
        const ok = document.execCommand('copy'); document.body.removeChild(ta); return ok;
      } catch { return false; }
    };
    try { await navigator.clipboard.writeText(t); toast('📋 Đã sao chép'); return; } catch { /* thử cách cũ */ }
    const ok = legacy();
    toast(ok ? '📋 Đã sao chép' : 'Không sao chép được — thử giữ tin rồi chọn Sao chép', ok ? undefined : 'danger');
  }, [toast]);

  // CHUYỂN TIẾP tin (chỉ nội bộ) sang 1 hội thoại khác — server lấy nội dung tin gốc.
  const forwardTo = useCallback(async (targetId) => {
    if (!forwardMsg) return;
    try {
      await sendMessage(targetId, '', { forwardFromId: forwardMsg._id });
      toast('Đã chuyển tiếp tin nhắn');
      setForwardMsg(null);
    } catch (e) { toast(e?.response?.data?.message || 'Chuyển tiếp thất bại', 'danger'); }
  }, [forwardMsg, toast]);

  // XOÁ CHỈ Ở PHÍA MÌNH (kiểu Zalo): ẩn tin khỏi màn của mình, NGƯỜI KHÁC VẪN THẤY.
  // Tin bất kỳ (của mình lẫn người khác), không giới hạn thời gian. Không cần socket — chỉ mình đổi.
  const doDeleteForMe = useCallback(async (m) => {
    if (!window.confirm('Xoá tin nhắn này CHỈ Ở PHÍA BẠN? Người khác vẫn nhìn thấy bình thường.')) return;
    try {
      await deleteMessageForMe(conv.id, m._id);
      setMessages((prev) => prev.filter((x) => String(x._id) !== String(m._id)));
    } catch (e) { toast(e?.response?.data?.message || 'Xoá thất bại', 'danger'); }
  }, [conv.id, toast]);

  // GHIM TIN lên đầu đoạn chat (kiểu Zalo, tối đa 3 — của CHUNG hội thoại, ai cũng ghim được).
  // refresh() nạp lại danh sách hội thoại -> dải ghim (đọc từ convs) tự cập nhật.
  const doPinMsg = useCallback(async (m) => {
    try {
      await pinMessage(conv.id, m._id);
      refresh();
      toast('📌 Đã ghim tin lên đầu đoạn chat');
    } catch (e) { toast(e?.response?.data?.message || 'Ghim thất bại', 'danger'); }
  }, [conv.id, toast, refresh]);
  const doUnpinMsg = useCallback(async (msgId) => {
    try {
      await unpinMessage(conv.id, msgId);
      refresh();
      toast('Đã bỏ ghim tin');
    } catch (e) { toast(e?.response?.data?.message || 'Bỏ ghim thất bại', 'danger'); }
  }, [conv.id, toast, refresh]);

  // CÔNG VIỆC trong chat (bảng công việc — mở được cả trên điện thoại). patchConv cập nhật NGAY,
  // socket 'chat:conversation' -> refresh() đồng bộ cho mọi thành viên.
  const patchTasks = useCallback((next) => patchConv(conv.id, { tasks: next }), [conv.id, patchConv]);
  const taskCreate = useCallback(async (payload) => {
    const r = await createChatTask(conv.id, payload);
    patchTasks([...(liveConv.tasks || []), r.task]);
  }, [conv.id, liveConv.tasks, patchTasks]);
  const taskAccept = useCallback(async (taskId) => {
    try { const r = await acceptChatTask(conv.id, taskId); patchTasks((liveConv.tasks || []).map((t) => (t.id === taskId ? r.task : t))); }
    catch (e) { toast(e?.response?.data?.message || 'Không tiếp nhận được', 'danger'); }
  }, [conv.id, liveConv.tasks, patchTasks, toast]);
  const taskComplete = useCallback(async (taskId, done) => {
    try { const r = await completeChatTask(conv.id, taskId, done); patchTasks((liveConv.tasks || []).map((t) => (t.id === taskId ? r.task : t))); }
    catch (e) { toast(e?.response?.data?.message || 'Không cập nhật được', 'danger'); }
  }, [conv.id, liveConv.tasks, patchTasks, toast]);
  const taskDelete = useCallback(async (taskId) => {
    if (!window.confirm('Xoá công việc này?')) return;
    try { await deleteChatTask(conv.id, taskId); patchTasks((liveConv.tasks || []).filter((t) => t.id !== taskId)); }
    catch (e) { toast(e?.response?.data?.message || 'Xoá thất bại', 'danger'); }
  }, [conv.id, liveConv.tasks, patchTasks, toast]);

  // Nhận sự kiện thu hồi từ người khác -> cập nhật ngay.
  useEffect(() => {
    const socket = getSocket();
    if (!socket) return;
    const onRecall = (e) => {
      if (String(e.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((x) => (String(x._id) === String(e._id) ? { ...x, recalled: true, text: '', attachments: [] } : x)));
    };
    const onEdited = (e) => {
      if (String(e.conversation) !== String(conv.id)) return;
      setMessages((prev) => prev.map((x) => (String(x._id) === String(e._id) ? { ...x, text: e.text, edited: true, editedAt: e.editedAt } : x)));
    };
    socket.on('chat:recall', onRecall);
    socket.on('chat:edited', onEdited);
    return () => { socket.off('chat:recall', onRecall); socket.off('chat:edited', onEdited); };
  }, [conv.id]);

  const startReply = useCallback((m) => {
    const preview = m.text || ((m.attachments || []).length ? 'đã gửi tệp đính kèm' : '');
    setReplyTo({ id: m._id, senderId: m.sender?._id, senderName: m.sender?.name || '', text: String(preview).slice(0, 200) });
    // TỰ CHÈN "@Tên " người được trả lời vào ĐẦU ô nhập (nhóm, tin người khác) — để thấy rõ
    // mình đang tag ai ngay trong nội dung. (Server vốn đã tự mention người được reply để báo,
    // nhưng tag không HIỆN trong tin nên người nhận tưởng không được tag.)
    const name = m.sender?.name;
    if (isGroup && name && String(m.sender?._id) !== myId) {
      const cur = inputRef.current ? inputRef.current.value : '';
      if (!cur.includes('@' + name)) putText('@' + name + ' ' + cur);
    }
    setTimeout(() => {
      const el = inputRef.current;
      if (el) { el.focus(); const n = el.value.length; el.setSelectionRange(n, n); } // con trỏ về CUỐI, sau tag
    }, 0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGroup, myId]);
  // Thả / đổi / bỏ cảm xúc 1 tin (chỉ tin người khác). Cập nhật lạc quan rồi gọi API.
  const doReact = useCallback((m, emoji) => {
    setReactPickerFor(null);
    setMessages((prev) => prev.map((mm) => {
      if (String(mm._id) !== String(m._id)) return mm;
      const others = (mm.reactions || []).filter((r) => String(r.user) !== myId);
      const cur = (mm.reactions || []).find((r) => String(r.user) === myId);
      const next = !emoji || (cur && cur.emoji === emoji) ? others : [...others, { user: myId, name: me.name, emoji }];
      return { ...mm, reactions: next };
    }));
    reactMessage(m._id, emoji).catch(() => listMessages(conv.id, { limit: 40 }).then(setMessages).catch(() => {}));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [myId, me.name, conv.id]);

  // Bấm vào trích dẫn -> cuộn tới tin gốc + nháy sáng.
  const flashMsg = (el) => {
    el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    el.classList.add('chatmsg-flash');
    setTimeout(() => el.classList.remove('chatmsg-flash'), 1400);
  };
  // NẠP TIẾP TIN MỚI HƠN sau khi đã nhảy tới 1 tin cũ (từ tìm kiếm): cuộn xuống chạm đáy CỬA SỔ ->
  // tải lô tin kế tiếp rồi GHÉP xuống dưới (đọc xuôi tự nhiên), thay vì nhảy phắt xuống tin cuối cùng.
  // Lô cuối (<1 lô) -> tắt cờ hasNewer; sát mép mới nhất (0 tin) -> thoát chế độ nhảy, về xem trực tiếp.
  const loadNewer = useCallback(async () => {
    if (loadingNewer || !jumpedRef.current) return;
    const arr = messagesRef.current;
    const last = arr[arr.length - 1];
    if (!last || !last.createdAt) return;
    setLoadingNewer(true);
    try {
      const more = await listMessages(conv.id, { after: last.createdAt, limit: 40 });
      if (more && more.length) {
        setMessages((prev) => {
          const ids = new Set(prev.map((m) => String(m._id)));
          const add = more.filter((m) => !ids.has(String(m._id)));
          return add.length ? [...prev, ...add] : prev;
        });
      }
      if (!more || more.length < 40) {
        setHasNewer(false);                                        // đã tới lô mới nhất -> hết tin nạp xuôi
        if (!more || more.length === 0) jumpedRef.current = false; // sát mép mới nhất -> về xem trực tiếp
      }
    } catch { /* mạng lỗi -> cuộn lại sẽ thử tiếp */ }
    finally { setLoadingNewer(false); }
  }, [conv.id, loadingNewer]);

  // Cuộn tới 1 tin (bấm trích dẫn reply / tin ghim). Nếu tin GỐC nằm trong phần tin cũ CHƯA tải,
  // tải dần từng lô tin cũ hơn tới khi gặp rồi mới cuộn (trước đây tin cũ -> im lặng không nhảy).
  const scrollToMsg = useCallback(async (id) => {
    if (!id) return;
    const here = document.getElementById('cmsg-' + id);
    if (here) return flashMsg(here);
    // NHẢY THEO MỐC (kiểu Facebook): tải thẳng CỬA SỔ tin quanh tin cần tìm (ngày của kết quả),
    // thay cả danh sách -> với tới BẤT KỲ tin nào tức thì, không lùi từng lô.
    setLoadingOlder(true);
    try {
      const win = await listMessages(conv.id, { around: id });
      if (win && win.length && win.some((m) => String(m._id) === String(id))) {
        setMessages(win);
        setHasOlder(true);        // còn tin cũ hơn phía trên cửa sổ
        setHasNewer(true);        // còn tin mới hơn phía dưới cửa sổ -> cuộn xuống nạp tiếp (KHÔNG nhảy tới tin cuối)
        jumpedRef.current = true; // đang ở giữa lịch sử -> tin mới nhất có thể chưa nạp
      } else {
        toast('Không tìm thấy tin (có thể đã bị thu hồi/xoá).', 'warn');
      }
    } catch { toast('Không tải được tin — thử lại.', 'danger'); }
    finally { setLoadingOlder(false); }
    requestAnimationFrame(() => setTimeout(() => {
      const el = document.getElementById('cmsg-' + id);
      if (el) flashMsg(el);
    }, 80));
  }, [conv.id, toast]);

  // Nhảy tới 1 tin từ ô "Tìm trong đoạn chat" (panel bên) — tự tải tin cũ nếu chưa có.
  useEffect(() => {
    const onJump = (e) => { if (String(e.detail?.convId) === String(conv.id)) scrollToMsg(e.detail.id); };
    window.addEventListener('ft:jump-msg', onJump);
    return () => window.removeEventListener('ft:jump-msg', onJump);
  }, [conv.id, scrollToMsg]);

  // Khớp @tên trong tin: PHẢI thêm tên MÌNH (mentionNames loại mình ra vì đó là menu tự-tag) để '@Tên-mình'
  // được nhận + tô. redNames = tên tô ĐỎ khi NGƯỜI KHÁC tag mình (tên mình / @Tất cả).
  const myName = me?.name || '';
  const matchNames = myName ? [myName, ...mentionNames] : mentionNames;
  const redNames = myName ? [myName, MENTION_ALL] : [MENTION_ALL];
  // Danh sách tin nhắn memo hoá -> gõ phím / chèn emoji không render lại cả danh sách.
  const messageEls = useMemo(
    () =>
      messages.map((m, _mi) => {
        const mine = String(m.sender?._id) === myId;
        const isFirstUnread = String(m._id) === String(firstUnreadId);
        // VẠCH NGÀY: hiện trước tin ĐẦU TIÊN của mỗi ngày (Hôm nay / Hôm qua / Thứ …, DD/MM/YYYY)
        // -> kéo xem tin cũ biết ngay tin của ngày nào, khỏi phải rê từng tin xem giờ.
        const prevMsg = messages[_mi - 1];
        const showDate = m.createdAt && (!prevMsg || !prevMsg.createdAt || new Date(prevMsg.createdAt).toDateString() !== new Date(m.createdAt).toDateString());
        // TIN HỆ THỐNG (ai vào/rời nhóm, nghỉ việc…): dòng nhỏ căn giữa, không avatar/menu/cảm xúc (kiểu Zalo/Telegram).
        if (m.system) {
          return (
            <div className="chatmsg-row chatmsg-sysrow" key={m._id} id={'cmsg-' + m._id}>
              {showDate && (<div className="chat-datedivider"><span>{fmtDayLabel(m.createdAt)}</span></div>)}
              <div className="chat-sysmsg"><span>{m.text}</span></div>
            </div>
          );
        }
        // GỘP tin nối tiếp cùng người (trong 5 phút, không vướng vạch ngày/tin-mới) -> ẩn avatar+tên, khít lại như Zalo.
        const grouped = prevMsg && String(prevMsg.sender?._id) === String(m.sender?._id) && prevMsg.createdAt && m.createdAt
          && (new Date(m.createdAt) - new Date(prevMsg.createdAt) < 5 * 60 * 1000) && !showDate && !isFirstUnread;
        const menuOpen = menuFor === m._id || reactPickerFor === m._id; // đang mở menu ⋯ / bảng cảm xúc
        return (
          <div className={'chatmsg-row' + (grouped ? ' grouped' : '') + (menuOpen ? ' menu-open' : '')} key={m._id} id={'cmsg-' + m._id}>
            {showDate && (
              <div className="chat-datedivider"><span>{fmtDayLabel(m.createdAt)}</span></div>
            )}
            {isFirstUnread && (
              <div className="chat-newdivider" ref={unreadRef}>
                <span>Tin nhắn mới</span>
              </div>
            )}
            <div className={'chatmsg' + (mine ? ' mine' : '') + (m.pending ? ' pending' : '') + (m.failed ? ' failed' : '')}>
              {!mine && isGroup && !grouped && (
                <div className="chatmsg-who chat-members-row--click" title="Xem thông tin nhân viên"
                  onClick={() => openProfile({ id: m.sender?._id, name: m.sender?.name, role: m.sender?.role })}>
                  <span className="chat-avatar xs"><AvaImg src={m.sender?.avatar} name={m.sender?.name} /></span>
                  {m.sender?.name}
                </div>
              )}
              <div className="chatmsg-line">
                <div className="chatmsg-content">
                  {m.forwardOf && !m.recalled && (
                    <div className="chatmsg-fwd" title="Tin được chuyển tiếp">↪ Đã chuyển tiếp{m.forwardOf.senderName ? ` từ ${m.forwardOf.senderName}` : ''}</div>
                  )}
                  {m.replyTo && (
                    <div className="chatmsg-quote" onClick={() => scrollToMsg(m.replyTo.message)} title="Xem tin gốc">
                      <span className="chatmsg-quote-name">{m.replyTo.senderName || 'Tin nhắn'}</span>
                      <span className="chatmsg-quote-text">{m.replyTo.text || 'đã gửi tệp đính kèm'}</span>
                    </div>
                  )}
                  {/* Thân tin: click (tin người khác) để hiện bảng cảm xúc; nút reply/ghi chú ở GÓC. */}
                  <div
                    className={'chatmsg-body' + (mine ? ' mine' : '')}
                    style={menuFor === m._id ? { zIndex: 40 } : undefined}
                    onClick={observer ? undefined : () => {
                      if (lpFired.current) return; // vừa GIỮ mở menu -> bỏ qua chạm (reset ở lần chạm kế tiếp)
                      if (!mine) setReactPickerFor((v) => (v === m._id ? null : m._id)); // chạm tin người khác -> thả cảm xúc
                    }}
                    onTouchStart={observer ? undefined : () => startLongPress(m._id)}
                    onTouchEnd={observer ? undefined : cancelLongPress}
                    onTouchMove={observer ? undefined : cancelLongPress}
                    onContextMenu={observer ? undefined : (e) => e.preventDefault()}
                    title={!mine && !observer ? 'Chạm để thả cảm xúc · Giữ để mở chức năng' : (!observer ? 'Giữ để mở chức năng' : undefined)}
                  >
                    {m.recalled && (
                      <div className="chatmsg-bubble chatmsg-recalled">🚫 Tin nhắn đã được thu hồi</div>
                    )}
                    {!m.recalled && m.text && (
                      <div className={'chatmsg-bubble' + (isBigEmoji(m.text) ? ' big-emoji' : '')}>
                        {renderWithMentions(m.text, matchNames, mine ? [] : redNames)}
                      </div>
                    )}
                    {!m.recalled && m.sticker && (
                      <div className="chatmsg-sticker"><Sticker id={m.sticker} /></div>
                    )}
                    {!m.recalled && m.poll && (
                      <div onClick={(e) => e.stopPropagation()}>
                        <PollCard
                          message={m}
                          myId={myId}
                          onVote={doVote}
                          onClose={doClosePoll}
                          canClose={String(m.sender?._id) === myId || me.role === 'GD' || me.role === 'CV'}
                          onPeek={setPeek}
                        />
                      </div>
                    )}
                    {(m.recalled ? [] : m.attachments || []).map((a, i) => (
                      <div className="chatmsg-att" key={i}>
                        <AttachmentView a={a} />
                      </div>
                    ))}
                    {/* Icon nhỏ ở góc bong bóng (hiện khi rê chuột). Tin đã thu hồi vẫn có góc —
                        để còn "Xoá ở phía tôi" dọn cái khung "đã thu hồi" khỏi màn mình.
                        GIÁM SÁT: ẩn hết (chỉ xem, không tác động). */}
                    {/* Kiểu Zalo: chỉ 2 nút gọn — Trả lời + ⋯ (menu đầy đủ). Cảm xúc = bấm vào tin. */}
                    {!observer && (
                    <div className={'chatmsg-corner' + (menuFor === m._id ? ' open' : '')}>
                      {!m.recalled && (
                        <button className="chatmsg-cbtn" title="Trả lời" onClick={(e) => { e.stopPropagation(); startReply(m); }}>↩</button>
                      )}
                      <button className="chatmsg-cbtn chatmsg-more" title="Thêm chức năng"
                        onClick={(e) => { e.stopPropagation(); setReactPickerFor(null); setMenuFor(m._id); }}>⋯</button>
                      {menuFor === m._id && (
                          <div className={'chatmsg-menu' + (mine ? ' mine' : '')} onClick={(e) => e.stopPropagation()}>
                            {!m.recalled && (
                              <>
                                <button className="chatmsg-menu-item" onClick={() => { startReply(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">↩</span> Trả lời</button>
                                {m.text && (
                                  <button className="chatmsg-menu-item" onClick={() => { doCopy(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">📋</span> Sao chép</button>
                                )}
                                <button className="chatmsg-menu-item" onClick={() => { setForwardMsg(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">↪</span> Chuyển tiếp</button>
                                <button className="chatmsg-menu-item" onClick={() => { saveNote(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">🔖</span> Lưu vào ghi chú</button>
                                <button className="chatmsg-menu-item" onClick={() => { setTaskMsg(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">📋</span> Tạo công việc</button>
                                {!m.pending && !m.failed && (
                                  <button className="chatmsg-menu-item" onClick={() => { if (pinnedIds.has(String(m._id))) doUnpinMsg(m._id); else doPinMsg(m); setMenuFor(null); }}>
                                    <span className="chatmsg-menu-ico">📌</span> {pinnedIds.has(String(m._id)) ? 'Bỏ ghim' : 'Ghim tin'}
                                  </button>
                                )}
                                {canEdit(m, myId) && (
                                  <button className="chatmsg-menu-item" onClick={() => { startEdit(m); }}><span className="chatmsg-menu-ico">✏️</span> Sửa</button>
                                )}
                                {canRemind(m, myId, isGroup) && (
                                  <button className="chatmsg-menu-item" onClick={() => { doRemind(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">🔔</span> Nhắc lại</button>
                                )}
                                {canRecall(m, myId) && (
                                  <button className="chatmsg-menu-item danger" onClick={() => { doRecall(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">🗑</span> Thu hồi</button>
                                )}
                              </>
                            )}
                            {!m.pending && !m.failed && (
                              <button className="chatmsg-menu-item danger" onClick={() => { doDeleteForMe(m); setMenuFor(null); }}><span className="chatmsg-menu-ico">✖</span> Xoá ở phía tôi</button>
                            )}
                          </div>
                      )}
                    </div>
                    )}
                    {/* Bảng chọn cảm xúc: chỉ hiện khi CLICK tin của người khác */}
                    {!mine && reactPickerFor === m._id && (
                      <div className="chatmsg-react-picker" onClick={(e) => e.stopPropagation()}>
                        {REACTION_EMOJIS.map((e) => (
                          <button key={e} type="button" className="chatmsg-react-opt" onClick={() => doReact(m, e)}>{e}</button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </div>
              {(m.reactions || []).length > 0 && (
                <div className="chatmsg-reacts">
                  {Object.entries(
                    (m.reactions || []).reduce((acc, r) => {
                      (acc[r.emoji] = acc[r.emoji] || []).push(r.name || 'Ai đó');
                      return acc;
                    }, {})
                  ).map(([emoji, names]) => {
                    const meReacted = (m.reactions || []).some((r) => String(r.user) === myId && r.emoji === emoji);
                    return (
                      <button
                        key={emoji}
                        type="button"
                        className={'chatmsg-react-badge' + (meReacted ? ' me' : '')}
                        title="Xem ai đã thả cảm xúc"
                        onClick={() =>
                          setPeek({
                            title: 'Cảm xúc',
                            lines: (m.reactions || []).map((r) => ({ icon: r.emoji, name: r.name || 'Ai đó' })),
                          })
                        }
                      >
                        <span>{emoji}</span> {names.length}
                      </button>
                    );
                  })}
                </div>
              )}
              {mine && m.failed ? (
                <div className="chatmsg-time" style={{ color: 'var(--danger)' }}>
                  Gửi lỗi · <button type="button" className="chatmsg-resend" onClick={() => resend(m)}>Gửi lại</button>
                </div>
              ) : (
                <div className="chatmsg-time">{m.pending ? (m.attachments?.length && m.progress != null && m.progress < 100 ? `Đang gửi… ${m.progress}%` : 'Đang gửi…') : (<>{fmtMsgTime(m.createdAt)}{m.edited && !m.recalled ? ' · đã sửa' : ''}</>)}</div>
              )}
            </div>
          </div>
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messages, myId, isGroup, observer, firstUnreadId, mentionKey, reactPickerFor, menuFor, doReact, doCopy, saveNote, resend, startReply, startEdit, scrollToMsg, openProfile, pinnedKey, doPinMsg, doUnpinMsg, doVote, doClosePoll]
  );

  // Dấu "đã xem": ai đã đọc TIN CUỐI THẬT của hội thoại (bất kỳ AI gửi) — hiện cho MỌI thành viên,
  // không chỉ người gửi. Loại NGƯỜI GỬI tin cuối (hiển nhiên đã đọc) + CHÍNH MÌNH (người xem đang nhìn).
  const seenBy = (() => {
    const lastReal = [...messages].reverse().find((m) => m._id && !m.pending && !m.failed);
    if (!lastReal) return [];
    const t = new Date(lastReal.createdAt).getTime();
    const senderId = String(lastReal.sender?._id || '');
    return (conv.members || [])
      .filter((mm) => String(mm.id) !== senderId && String(mm.id) !== myId && (readMap[String(mm.id)] || 0) >= t)
      .map((mm) => mm.name);
  })();

  // Kéo giãn cửa sổ từ góc trên-trái (cửa sổ neo góc dưới-phải). Bỏ qua trên mobile (đang full màn).
  const startResize = useCallback((e) => {
    if (window.innerWidth <= 980) return;
    e.preventDefault();
    e.stopPropagation();
    const rect = panelRef.current?.getBoundingClientRect();
    const startW = rect?.width || 372;
    const startH = rect?.height || 520;
    const startX = e.clientX;
    const startY = e.clientY;
    const onMove = (ev) => {
      const w = Math.min(720, Math.max(300, startW + (startX - ev.clientX)));
      const h = Math.min(Math.round(window.innerHeight * 0.92), Math.max(360, startH + (startY - ev.clientY)));
      setSize({ w, h });
    };
    const onUp = () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.userSelect = '';
      setSize((s) => { if (s) { try { localStorage.setItem('ft_chat_size', JSON.stringify(s)); } catch {} } return s; });
    };
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  // Kích thước áp dụng (chỉ khi không thu nhỏ; mobile để CSS lo full màn).
  const panelStyle = size && !minimized ? { width: size.w, height: size.h } : undefined;

  return (
    <div
      ref={panelRef}
      style={panelStyle}
      className={'chatpanel chatpanel-conv' + (front ? ' front' : '') + (minimized ? ' min' : '') + (blink ? ' chatpanel-blink' : '') + (buzzing ? ' chatpanel-buzz' : '')}
      /* Bấm vào cửa sổ đang nhấp nháy -> tắt nhấp nháy (đã thấy tin). */
      onMouseDown={() => blink && setBlink(false)}
    >
      {!minimized && <div className="chatpanel-resize" onMouseDown={startResize} title="Kéo để thay đổi kích thước" />}
      {peek && (
        <div className="chat-peek-overlay" onClick={() => setPeek(null)}>
          <div className="chat-peek" onClick={(e) => e.stopPropagation()}>
            <div className="chat-peek-head">
              <b>{peek.title} · {peek.lines.length}</b>
              <button type="button" className="chat-members-x" onClick={() => setPeek(null)} title="Đóng">×</button>
            </div>
            <div className="chat-peek-list">
              {peek.lines.map((l, i) => (
                <div key={i} className="chat-peek-row">
                  <span className="chat-peek-icon">{l.icon}</span>
                  <span className="chat-peek-name">{l.name}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="chathead" onClick={() => minimized && toggleMinimize(false)}>
        <button className="chathead-btn chathead-back" onClick={(e) => { e.stopPropagation(); onBack ? onBack() : closeWindow(conv.id); }} title="Quay lại danh sách">‹</button>
        <span className={'chat-avatar sm chathead-ava' + (isGroup ? ' group' : '')} style={{ position: 'relative' }}>
          {isGroup ? '👥' : <AvaImg src={conv.other?.avatar} name={conv.name} />}
          {!isGroup && conv.other?.id && onlineIds?.has(String(conv.other.id)) && <span className="chat-online-dot" title="Đang online" />}
        </span>
        <div
          className="chathead-title"
          onClick={onTitleClick ? (e) => { e.stopPropagation(); onTitleClick(); } : undefined}
          style={onTitleClick ? { cursor: 'pointer' } : undefined}
          title={onTitleClick ? 'Bấm để mở/đóng bảng thông tin' : undefined}
        >
          {conv.name}
          {minimized && minUnread > 0 && <span className="chathead-unread" style={{ marginLeft: 8 }}>{minUnread > 99 ? '99+' : minUnread}</span>}
          {!minimized && isGroup && <span className="small" style={{ opacity: .85, fontWeight: 500 }}> · {(conv.members || []).length} thành viên</span>}
          {!minimized && !isGroup && conv.other?.resigned && <span className="small" style={{ color: '#fecaca', fontWeight: 600 }}> · Đã nghỉ việc</span>}
          {!minimized && !isGroup && !conv.other?.resigned && conv.other?.id && onlineIds?.has(String(conv.other.id)) && <span className="small" style={{ color: 'var(--ok,#16a34a)', fontWeight: 500 }}> · Đang hoạt động</span>}
          {!minimized && !isGroup && !conv.other?.resigned && conv.other?.id && !onlineIds?.has(String(conv.other.id)) && conv.other?.lastSeenAt && <span className="small" style={{ opacity: .8, fontWeight: 500 }}> · Truy cập {sinceLabel(conv.other.lastSeenAt)}</span>}
        </div>
        {!minimized && !observer && (
          <button className="chathead-btn chathead-buzz" onClick={(e) => { e.stopPropagation(); sendBuzz(); }}
            title="Buzz! — rung chuông gọi (rung màn hình + âm thanh ngay)">
            🐝
          </button>
        )}
        {!minimized && !observer && (
          <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); doMute(); }}
            title={muted ? 'Đang tắt âm — bấm để bật lại thông báo' : 'Tắt âm thông báo hội thoại này'}>
            {muted ? '🔕' : '🔔'}
          </button>
        )}
        {!minimized && !observer && (
          <button className="chathead-btn chathead-tasks" onClick={(e) => { e.stopPropagation(); setShowTasks((v) => !v); setShowMembers(false); }} title="Công việc trong chat">
            📋{openTaskCount(tasks) > 0 && <span className="chathead-badge">{openTaskCount(tasks)}</span>}
          </button>
        )}
        {isGroup && !minimized && (
          <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); setShowMembers((v) => !v); setShowTasks(false); }} title="Xem thành viên nhóm">
            👥
          </button>
        )}
        <button className="chathead-btn chathead-min" onClick={(e) => { e.stopPropagation(); toggleMinimize(); }} title={minimized ? 'Mở lại' : 'Thu nhỏ'}>
          {minimized ? '▢' : '—'}
        </button>
        <button className="chathead-btn" onClick={(e) => { e.stopPropagation(); if (isMobile()) setOpen(false); else closeWindow(conv.id); }} title="Đóng cửa sổ chat">
          ×
        </button>
      </div>
      {buzzing && !minimized && <div className="chat-buzz-banner">🐝 BUZZ!!!</div>}
      {showTasks && !minimized && (
        <div className="chat-members chat-taskspanel">
          <div className="chat-members-head">
            <b>📋 Công việc{openTaskCount(tasks) ? ` (${openTaskCount(tasks)})` : ''}</b>
            <button className="chat-members-x" onClick={() => setShowTasks(false)} title="Đóng">×</button>
          </div>
          <div className="chat-taskspanel-body">
            <ChatTasksPanel tasks={tasks} assigneeOptions={taskAssignees} me={me}
              onCreate={taskCreate} onAccept={taskAccept} onComplete={taskComplete} onDelete={taskDelete} />
          </div>
        </div>
      )}
      {isGroup && showMembers && !minimized && (
        <div className="chat-members">
          <div className="chat-members-head">
            <b>Thành viên ({(conv.members || []).length})</b>
            <div className="row" style={{ gap: 6 }}>
              {canRemoveMember && <button className="btn sm" disabled={memberBusy} onClick={renameGroupChat} title="Đổi tên nhóm">✎ Đổi tên</button>}
              {canAddMember && (
                <button className="btn sm" onClick={openAddMember}>{showAddMember ? '✕ Đóng' : '＋ Thêm'}</button>
              )}
              {isCreator && (
                <button className="btn sm" style={{ color: 'var(--danger)', borderColor: 'var(--danger)' }} disabled={memberBusy} onClick={deleteGroupChatHandler} title="Xoá (giải tán) nhóm — không khôi phục được">🗑 Xoá nhóm</button>
              )}
              <button className="chat-members-x" onClick={() => setShowMembers(false)} title="Đóng">×</button>
            </div>
          </div>

          {canAddMember && showAddMember && (() => {
            const memberIdSet = new Set((conv.members || []).map((m) => String(m.id)));
            const nq = addQ.trim().toLowerCase();
            const options = allUsers
              .filter((u) => !memberIdSet.has(String(u.id)))
              .filter((u) => !nq || String(u.name).toLowerCase().includes(nq))
              .slice(0, 30);
            return (
              <div className="chat-members-add">
                <input autoFocus value={addQ} onChange={(e) => setAddQ(e.target.value)} placeholder="🔍 Tìm nhân viên để thêm…" style={{ width: '100%' }} />
                <div className="chat-members-addlist">
                  {options.length ? options.map((u) => (
                    <div key={u.id} className="chat-members-row" style={{ cursor: memberBusy ? 'wait' : 'pointer' }} onClick={() => addMember(u.id)}>
                      <span className="chat-avatar sm"><AvaImg src={u.avatar} name={u.name} /></span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div className="chat-members-name">{u.name}</div>
                        <div className="small muted">{(u.departments || []).join(', ') || u.role}</div>
                      </div>
                      <span className="small" style={{ color: 'var(--accent2)' }}>Thêm</span>
                    </div>
                  )) : <div className="small muted" style={{ padding: '6px 12px' }}>Không còn ai để thêm.</div>}
                </div>
              </div>
            );
          })()}

          <div className="chat-members-list">
            {[...(conv.members || [])]
              .sort((a, b) => (onlineIds?.has(String(b.id)) ? 1 : 0) - (onlineIds?.has(String(a.id)) ? 1 : 0) || String(a.name).localeCompare(String(b.name), 'vi'))
              .map((mm) => {
                const on = onlineIds?.has(String(mm.id));
                const memberIsCreator = String(conv.createdBy || '') === String(mm.id);
                return (
                  <div key={mm.id} className="chat-members-row chat-members-row--click" onClick={() => openProfile(mm)} title="Xem thông tin nhân viên">
                    <span className="chat-avatar sm" style={{ position: 'relative' }}>
                      <AvaImg src={mm.avatar} name={mm.name} />
                      {on && <span className="chat-online-dot" title="Đang online" />}
                    </span>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div className="chat-members-name">
                        {mm.name}{String(mm.id) === myId ? ' (bạn)' : ''}
                        {memberIsCreator
                          ? <span className="chat-leader-badge" title="Trưởng nhóm — người tạo nhóm, được thêm/bớt thành viên và xoá nhóm">👑 Trưởng nhóm</span>
                          : mm.isDeputy ? <span className="small muted"> · Phó phòng</span> : mm.role === 'TP' ? <span className="small muted"> · TP</span> : mm.role === 'GD' ? <span className="small muted"> · GĐ</span> : null}
                      </div>
                      <div className="small" style={{ color: mm.resigned ? 'var(--danger)' : on ? 'var(--ok,#16a34a)' : 'var(--muted)' }}>
                        {mm.resigned ? 'Đã nghỉ việc' : on ? 'Đang online' : 'Ngoại tuyến'}
                      </div>
                    </div>
                    {canRemoveMember && !memberIsCreator && (
                      <button className="chat-members-remove" title={`Xoá ${mm.name} khỏi nhóm`} disabled={memberBusy} onClick={(e) => { e.stopPropagation(); removeMember(mm.id, mm.name); }}>✕</button>
                    )}
                  </div>
                );
              })}
          </div>
        </div>
      )}
      {profileUser && (
        <div className="chat-profile-overlay" onClick={() => setProfileUser(null)}>
          <div className="chat-profile-card" onClick={(e) => e.stopPropagation()}>
            <button className="chat-members-x" style={{ position: 'absolute', top: 8, right: 10 }} onClick={() => setProfileUser(null)} title="Đóng">×</button>
            <div className="chat-avatar" style={{ width: 56, height: 56, fontSize: 22, position: 'relative' }}>
              <AvaImg src={profileUser.avatar} name={profileUser.name} />
              {onlineIds?.has(String(profileUser.id)) && <span className="chat-online-dot" title="Đang online" />}
            </div>
            <div style={{ fontWeight: 700, fontSize: 16, marginTop: 8 }}>{profileUser.name}{String(profileUser.id) === myId ? ' (bạn)' : ''}</div>
            <div className="small muted">{ROLE_LABEL[profileUser.role] || 'Nhân viên'}</div>
            <div className="small" style={{ marginTop: 6 }}>
              <b>Phòng ban:</b> {(profileUser.departments || []).length ? profileUser.departments.join(', ') : '—'}
            </div>
            <div className="small" style={{ marginTop: 2, color: profileUser.resigned ? 'var(--danger)' : onlineIds?.has(String(profileUser.id)) ? 'var(--ok,#16a34a)' : 'var(--muted)' }}>
              {profileUser.resigned ? '⛔ Đã nghỉ việc' : onlineIds?.has(String(profileUser.id)) ? '● Đang online' : '○ Ngoại tuyến'}
            </div>
            {String(profileUser.id) !== myId && (
              <button className="btn" style={{ marginTop: 14, width: '100%' }} onClick={chatWithProfile}>💬 Nhắn tin</button>
            )}
          </div>
        </div>
      )}
      {/* DẢI TIN GHIM (kiểu Zalo): tối đa 3, của CHUNG hội thoại. Mặc định thu gọn còn 1 dòng;
          bấm dòng ghim -> cuộn tới tin gốc (nếu tin còn trong khung); GIÁM SÁT chỉ xem, không bỏ ghim. */}
      {!minimized && pinnedMsgs.length > 0 && (
        <div className="chatpin-bar">
          {(pinsOpen ? pinnedMsgs : pinnedMsgs.slice(0, 1)).map((p) => (
            <div key={p.msgId} className="chatpin-row" onClick={() => scrollToMsg(p.msgId)} title="Bấm để cuộn tới tin gốc">
              <span className="chatpin-ico">📌</span>
              <span className="chatpin-text"><b>{p.sender}:</b> {p.text}</span>
              {!observer && (
                <button type="button" className="chatpin-x" title="Bỏ ghim tin này"
                  onClick={(e) => { e.stopPropagation(); doUnpinMsg(p.msgId); }}>×</button>
              )}
            </div>
          ))}
          {pinnedMsgs.length > 1 && (
            <button type="button" className="chatpin-more" onClick={() => setPinsOpen((v) => !v)}>
              {pinsOpen ? 'Thu gọn ▴' : `+${pinnedMsgs.length - 1} tin ghim khác ▾`}
            </button>
          )}
        </div>
      )}
      <div className="chatbody">
        <div
          className={'chatthread' + (dragOver ? ' dragover' : '')}
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={(e) => {
            if (e.currentTarget === e.target) setDragOver(false);
          }}
          onDrop={onDrop}
        >
          {dragOver && <div className="chat-droplay">Thả tệp để gửi</div>}
          <div
            className="chatmsgs"
            ref={msgsRef}
            onScroll={(e) => {
              const near = isNearBottom(); setScrolledUp(!near);
              // ĐANG Ở CỬA SỔ LỊCH SỬ (đã nhảy tới tin cũ) mà cuộn tới đáy cửa sổ VÀ CÒN tin mới hơn:
              // nạp TIẾP lô sau để đọc xuôi — KHÔNG nhảy phắt xuống tin cuối, chưa vội "dính đáy".
              if (near && jumpedRef.current && hasNewer && !loadingNewer) {
                stickBottomRef.current = false;
                loadNewer();
              } else {
                stickBottomRef.current = near; // rời đáy (đọc tin cũ) -> thôi tự kéo xuống; về đáy -> dính lại
                if (near) { if (newBelow) setNewBelow(0); jumpedRef.current = false; } // tới đáy THẬT = về xem trực tiếp
              }
              // CUỘN LÊN GẦN ĐỈNH -> tự tải tin cũ hơn (khỏi bấm nút), giữ nguyên chỗ đọc. Ngưỡng 600px (~1
              // màn hình) để nạp SỚM trước khi chạm sát đỉnh -> cảm giác cuộn vô tận, không "khựng" ở đầu.
              if (e.currentTarget.scrollTop < 600 && hasOlder && !loadingOlder) loadOlder();
            }}
            onClick={(e) => { if (e.target.classList.contains('chat-att-img')) setImgView({ src: e.target.dataset.full || e.target.src, name: e.target.alt }); }}
          >
            {/* Nút tải TIN CŨ HƠN — tin cũ vẫn nằm trên máy chủ, bấm để lùi thêm 40 tin/lần. */}
            {!loadingMsgs && hasOlder && (
              <button type="button" className="chat-loadolder" disabled={loadingOlder} onClick={() => loadOlder(true)}>
                {loadingOlder ? 'Đang tải…' : '↑ Xem tin cũ hơn'}
              </button>
            )}
            {loadingMsgs ? <div className="chatempty">Đang tải…</div> : messageEls}
            {/* Dấu "Đã xem" bám tin ĐÃ GỬI THẬT cuối cùng. Nếu tin cuối là tin ĐANG GỬI/LỖI thì KHÔNG
                hiện (tránh trông như tin lỗi đã được xem — seenBy vốn tính theo tin thật, không phải temp). */}
            {seenBy.length > 0 && !(messages[messages.length - 1]?.pending || messages[messages.length - 1]?.failed) && (
              <div
                className="chatmsg-seen"
                title={isGroup ? 'Bấm để xem ai đã đọc' : seenBy.join(', ')}
                style={isGroup ? { cursor: 'pointer' } : undefined}
                onClick={isGroup ? () => setPeek({ title: 'Đã đọc', lines: seenBy.map((n) => ({ icon: '✓', name: n })) }) : undefined}
              >
                ✓ {isGroup ? `Đã xem bởi ${seenBy.length}` : 'Đã xem'}
              </div>
            )}
            {typingNames.length > 0 && (
              <div className="chat-typing">
                <span className="chat-typing-dots"><i /><i /><i /></span>
                <span className="small muted">{isGroup ? `${typingNames.join(', ')} đang gõ…` : 'đang gõ…'}</span>
              </div>
            )}
            <div ref={endRef} />
          </div>

          {/* Nút NHẢY (Zalo-style) góc trên phải: có tin tag mình -> 2 pill; chỉ chưa đọc -> nút tròn + số.
              Bấm -> cuộn tới tin CŨ NHẤT chưa đọc / tag mình để kéo xuống đọc. Tự ẩn khi cuộn/gõ. */}
          {showJump && firstUnreadId && (
            <div className="chat-unread-jump">
              {firstUnreadMentionId ? (
                <>
                  <button type="button" className="chat-unread-pill" onClick={() => { setShowJump(false); scrollToMsg(firstUnreadId); }}>
                    <ChevronsUp /> Tin chưa đọc
                  </button>
                  <button type="button" className="chat-unread-pill mention" onClick={() => { setShowJump(false); scrollToMsg(firstUnreadMentionId); }}>
                    <ChevronsUp /> Nhắc đến bạn
                  </button>
                </>
              ) : (
                <button type="button" className="chat-unread-circle" aria-label="Tới tin chưa đọc" onClick={() => { setShowJump(false); scrollToMsg(firstUnreadId); }}>
                  <span className="circ"><ChevronsUp /></span>
                  <span className="chat-unread-count">{unreadSnapRef.current > 99 ? '99+' : unreadSnapRef.current}</span>
                </button>
              )}
            </div>
          )}

          {/* Đang cuộn xa đáy: LUÔN có nút về tin mới nhất (nhóm bận, tin dồn liên tục khó kéo tay).
              Có tin mới -> hiện số; không -> chỉ mũi tên về hôm nay. */}
          {(newBelow > 0 || scrolledUp) && (
            <button type="button" className="chat-jump-latest" onClick={jumpToLatest}>
              {newBelow > 0 ? `↓ ${newBelow} tin nhắn mới` : '↓ Tin mới nhất'}
            </button>
          )}

          {atts.length > 0 && (
            <div className="chat-att-strip">
              {atts.map((a, i) => (
                <div className="chat-att-chip" key={i}>
                  {a.preview ? <img src={a.preview} alt="" /> : <span className="chat-att-doc">📎 {a.name}</span>}
                  <button type="button" className="chat-att-x" onClick={() => setAtts((prev) => prev.filter((_, j) => j !== i))}>
                    ×
                  </button>
                </div>
              ))}
            </div>
          )}

          {editingId && (
            <div className="chat-reply-bar">
              <div className="chat-reply-info">
                <span className="chat-reply-label">✏️ Đang sửa tin nhắn</span>
                <span className="chat-reply-text">Gửi để lưu · hoặc bấm × để huỷ</span>
              </div>
              <button type="button" className="chat-reply-x" onClick={() => { setEditingId(null); putText(''); }} title="Huỷ sửa">×</button>
            </div>
          )}
          {replyTo && !editingId && (
            <div className="chat-reply-bar">
              <div className="chat-reply-info">
                <span className="chat-reply-label">↩ Trả lời {replyTo.senderName}</span>
                <span className="chat-reply-text">{replyTo.text || 'đã gửi tệp đính kèm'}</span>
              </div>
              <button type="button" className="chat-reply-x" onClick={() => setReplyTo(null)} title="Huỷ trả lời">×</button>
            </div>
          )}

          {imgView && <ImageLightbox src={imgView.src} filename={imgView.name} onClose={() => setImgView(null)} />}

          {forwardMsg && (
            <div className="chat-lightbox" onClick={() => setForwardMsg(null)}>
              <div className="chat-fwd-modal" onClick={(e) => e.stopPropagation()}>
                <div className="chat-fwd-head">
                  <b>↪ Chuyển tiếp tới</b>
                  <button type="button" className="chatmsg-cbtn" onClick={() => setForwardMsg(null)} title="Đóng">✕</button>
                </div>
                <div className="chat-fwd-preview">
                  {(forwardMsg.text || '').slice(0, 120) || (forwardMsg.attachments?.length ? 'đã gửi tệp đính kèm' : '')}
                </div>
                <div className="chat-fwd-list">
                  {convs.filter((c) => String(c.id) !== String(conv.id)).map((c) => (
                    <button type="button" key={c.id} className="chat-fwd-item" onClick={() => forwardTo(c.id)}>
                      {c.name}{c.type === 'group' ? ' (nhóm)' : ''}
                    </button>
                  ))}
                  {convs.filter((c) => String(c.id) !== String(conv.id)).length === 0 && (
                    <div className="empty">Chưa có hội thoại khác</div>
                  )}
                </div>
              </div>
            </div>
          )}

          {taskMsg && (
            <ChatTaskModal
              msg={taskMsg}
              assigneeOptions={(conv.members || []).map((mm) => ({ id: mm.id, name: mm.name }))}
              me={me}
              onCreate={async (payload) => { await createChatTask(conv.id, payload); refresh(); toast('📋 Đã tạo công việc — xem ở bảng thông tin (mở trang Tin nhắn để hoàn thành)'); }}
              onClose={() => setTaskMsg(null)}
            />
          )}

          {observer ? (
            <div className="chatcompose chat-observer">
              <span>👁 Chế độ giám sát — chỉ xem, không thể nhắn hay tương tác</span>
              <button type="button" className="btn sm ghost" onClick={() => listMessages(conv.id, { limit: 40 }).then(setMessages).catch(() => {})}>
                🔄 Tải tin mới
              </button>
            </div>
          ) : (
          <form className="chatcompose" onSubmit={submit}>
            {suggestions.length > 0 && (
              <div className="chat-mention-pop">
                {suggestions.slice(0, 8).map((o) => (
                  <div key={o.id} className="chat-mention-item" onMouseDown={(e) => e.preventDefault()} onClick={() => pickMention(o)}>
                    <span className={'chat-avatar sm' + (o.id === 'all' ? ' group' : '')}>{o.id === 'all' ? '📣' : <AvaImg src={o.avatar} name={o.name} />}</span>
                    {o.id === 'all' ? <b>@Tất cả thành viên</b> : o.name}
                  </div>
                ))}
              </div>
            )}
            {showEmoji && <EmojiPicker onPick={insertEmoji} onSend={sendSticker} />}
            {showPoll && isGroup && <PollComposer onCancel={() => setShowPoll(false)} onCreate={sendPoll} />}
            {pastePrompt && (
              <div className="paste-choice">
                <div className="paste-choice-msg">Nội dung vừa dán có <b>cả ảnh và văn bản</b>. Bạn muốn gửi dạng nào?</div>
                <div className="paste-choice-btns">
                  <button type="button" className="btn sm" onClick={choosePasteImage}>🖼 Gửi ảnh</button>
                  <button type="button" className="btn sm ghost" onClick={choosePasteText}>📝 Gửi văn bản</button>
                  <button type="button" className="paste-choice-x" title="Bỏ qua" onClick={() => setPastePrompt(null)}>×</button>
                </div>
              </div>
            )}
            <button
              ref={emojiBtnRef}
              type="button"
              className={'chathead-btn chat-attbtn' + (showEmoji ? ' on' : '')}
              title="Sticker & emoji"
              onClick={() => { setShowPoll(false); setShowEmoji((v) => !v); }}
            >
              😊
            </button>
            <button type="button" className="chathead-btn chat-attbtn" title="Đính kèm tệp" onClick={() => fileRef.current?.click()}>
              📎
            </button>
            {isGroup && (
              <button
                type="button"
                className={'chathead-btn chat-attbtn' + (showPoll ? ' on' : '')}
                title="Tạo bình chọn"
                onClick={() => { setShowEmoji(false); setShowPoll((v) => !v); }}
              >
                📊
              </button>
            )}
            <input ref={fileRef} type="file" multiple style={{ display: 'none' }} onChange={(e) => addFiles(e.target.files)} />
            {/* Ô nhập NHIỀU DÒNG: tự cao theo nội dung (tối đa ~5 dòng — xem effect chỉnh height).
                MÁY TÍNH: Enter = GỬI, Shift+Enter = xuống dòng (Giám đốc chốt lại 7/2026).
                ĐIỆN THOẠI: Enter = xuống dòng như bàn phím ảo, gửi bằng nút "Gửi". */}
            <textarea
              ref={inputRef}
              rows={1}
              defaultValue={initialTextRef.current}
              onChange={onChange}
              onPaste={onPaste}
              onFocus={() => setComposerFocused(conv.id, true)}
              onBlur={() => setComposerFocused(conv.id, false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent?.isComposing && !isMobile()) {
                  e.preventDefault();
                  e.currentTarget.form?.requestSubmit();
                }
              }}
              placeholder="Nhập tin nhắn…"
              /* Cửa sổ TỰ BUNG do có tin mới: KHÔNG tự focus — giữ con trỏ ở hội thoại đang gõ,
                 tránh gõ nhầm nhóm. Chỉ cửa sổ do người dùng chủ động mở mới nhận con trỏ.
                 MOBILE: không auto-focus để bàn phím KHÔNG bật, thấy tin chưa đọc; bấm ô mới hiện. */
              autoFocus={front && !conv.autoOpened && !isMobile()}
            />
            <button type="submit" className="btn sm" disabled={sending || (!text.trim() && !atts.length)}>
              {sending ? '…' : 'Gửi'}
            </button>
          </form>
          )}
        </div>
      </div>
    </div>
  );
}

function Settings() {
  const { soundOn, setSoundOn, notifOn, setNotifOn, me } = useChat();
  const { setMe } = useAuth();
  const toast = useToast();
  const [perm, setPerm] = useState(typeof Notification !== 'undefined' ? Notification.permission : 'unsupported');
  const [testing, setTesting] = useState(false);
  // TẮT/BẬT thông báo hệ thống KHÁCH theo TỪNG LUỒNG (kd/cskh/dvkh) — CHỈ TP/GĐ. mutedSvc = luồng đang TẮT.
  // (Trước đây nằm ở đầu danh sách chat KH; chuyển vào đây cho gọn.)
  const canMuteCust = canMuteKh(me);
  const [mutedSvc, setMutedSvc] = useState(me?.custMutedSvc || []);
  useEffect(() => { setMutedSvc(me?.custMutedSvc || []); }, [me?.custMutedSvc]);
  // Cờ MASTER "tắt tất cả" (custPushMutedAll) — ghi đè per-channel. Đưa vào đây để KHÔNG bị ẩn: trước
  // GĐ bật per-channel ở widget mà master (đặt ở trang Tin nhắn) vẫn tắt -> không kêu gì (lỗi đã gặp).
  const [mutedAll, setMutedAll] = useState(!!me?.custPushMutedAll);
  useEffect(() => { setMutedAll(!!me?.custPushMutedAll); }, [me?.custPushMutedAll]);
  const toggleMuteAll = async () => {
    const next = !mutedAll;
    setMutedAll(next);
    try {
      const r = await custMuteAll(next);
      setMutedAll(!!r.muted);
      setMe({ ...me, custPushMutedAll: !!r.muted });
    } catch { setMutedAll(!next); }
  };
  const toggleChannel = async (svcType) => {
    const willMute = !mutedSvc.includes(svcType); // đang BẬT -> bấm để TẮT luồng này
    const next = willMute ? [...mutedSvc, svcType] : mutedSvc.filter((s) => s !== svcType);
    setMutedSvc(next); // cập nhật ngay cho mượt
    try {
      const r = await custMuteChannel(svcType, willMute);
      const svc = r.mutedSvc || next;
      setMutedSvc(svc);
      // BE tự tắt cờ master khi BẬT 1 luồng -> đồng bộ về FE để nút master hiện đúng.
      const allOff = r.custPushMutedAll !== undefined ? !!r.custPushMutedAll : mutedAll;
      setMutedAll(allOff);
      setMe({ ...me, custMutedSvc: svc, custPushMutedAll: allOff }); // giữ trạng thái kể cả khi remount
    } catch { setMutedSvc(mutedSvc); } // lỗi -> hoàn lại
  };

  async function askPermission() {
    if (typeof Notification === 'undefined') return;
    const p = await Notification.requestPermission();
    setPerm(p);
    if (p === 'granted') {
      const { registerPush } = await import('../push');
      await registerPush();
      toast('Đã bật thông báo Chrome');
    }
  }

  async function sendTest() {
    setTesting(true);
    try {
      const { registerPush } = await import('../push');
      await registerPush(); // đảm bảo đã đăng ký subscription
      const { testPush } = await import('../api/chat');
      const r = await testPush();
      if (r.subs === 0) toast('Chưa có thiết bị nào đăng ký nhận. Hãy bật thông báo Chrome trước.', 'warn');
      else if (r.sent === 0) toast('Gửi thất bại — kiểm tra quyền thông báo của trình duyệt.', 'danger');
      else toast('Đã gửi thông báo thử. Thử đóng tab rồi gửi lại để kiểm tra.');
    } catch {
      toast('Không gửi được thông báo thử', 'danger');
    } finally {
      setTesting(false);
    }
  }

  return (
    <div className="chatnew">
      <label className="chat-set-row">
        <span>🔔 Thông báo trên Chrome</span>
        <input type="checkbox" checked={notifOn} onChange={(e) => setNotifOn(e.target.checked)} />
      </label>
      <label className="chat-set-row">
        <span>🔊 Âm thanh báo tin nhắn</span>
        <input type="checkbox" checked={soundOn} onChange={(e) => setSoundOn(e.target.checked)} />
      </label>

      {canMuteCust && (
        <div style={{ marginTop: 6, paddingTop: 10, borderTop: '1px solid var(--border, #e5e7eb)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 10 }}>
            <span className="small" style={{ fontWeight: 600 }}>🔔 Thông báo tất cả khách</span>
            <button type="button" onClick={toggleMuteAll}
              title={mutedAll ? 'Đang TẮT toàn bộ thông báo khách — bấm để bật lại' : 'Đang BẬT — bấm để tắt toàn bộ'}
              style={{ fontSize: 12, padding: '4px 12px', borderRadius: 999, fontWeight: 700, border: 'none', cursor: 'pointer', color: '#fff', background: mutedAll ? 'var(--danger, #ef4444)' : 'var(--accent, #4f46e5)' }}>
              {mutedAll ? '🔕 Đang tắt' : '🔔 Đang bật'}
            </button>
          </div>
          {mutedAll && <div className="small" style={{ color: 'var(--danger, #ef4444)', marginBottom: 10 }}>⚠ Đang tắt TẤT CẢ — các luồng bên dưới sẽ không nổi thông báo cho tới khi bật lại.</div>}
          <div className="small" style={{ fontWeight: 600, marginBottom: 8 }}>🔔 Thông báo hệ thống khách theo luồng</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {SVC_ORDER.map((t) => {
              const on = !mutedSvc.includes(t); // BẬT = có nổi thông báo hệ thống luồng này
              return (
                <button key={t} type="button" className={'cust-notif-chip' + (on ? ' on' : '')}
                  onClick={() => toggleChannel(t)}
                  title={(on ? 'Đang BẬT' : 'Đang TẮT') + ` thông báo hệ thống luồng ${SVC_LABEL[t]} — bấm để ` + (on ? 'tắt' : 'bật')}>
                  {on ? '🔔' : '🔕'} {SVC_LABEL[t]}
                </button>
              );
            })}
          </div>
          <div className="small muted" style={{ marginTop: 8 }}>Tắt luồng nào thì không nổi thông báo/âm luồng đó (vẫn đếm chưa đọc).</div>
        </div>
      )}

      <div className="chat-set-perm">
        Quyền thông báo trình duyệt:{' '}
        <b style={{ color: perm === 'granted' ? 'var(--accent2)' : 'var(--warn)' }}>
          {perm === 'granted' ? 'Đã bật' : perm === 'denied' ? 'Bị chặn' : 'Chưa bật'}
        </b>
        {perm !== 'granted' && (
          <button className="btn sm" style={{ marginTop: 10, width: '100%' }} onClick={askPermission}>
            Bật thông báo Chrome
          </button>
        )}
        {perm === 'denied' && (
          <div className="small muted" style={{ marginTop: 8 }}>
            Bạn đã chặn thông báo — hãy mở khóa ở biểu tượng 🔒 trên thanh địa chỉ Chrome rồi tải lại trang.
          </div>
        )}
      </div>
      <button className="btn ghost" style={{ marginTop: 14, width: '100%' }} disabled={testing} onClick={sendTest}>
        {testing ? 'Đang gửi…' : '🔔 Gửi thông báo thử'}
      </button>
      <div className="small muted" style={{ marginTop: 12 }}>
        Âm thanh phát khi có tin nhắn mới mà bạn chưa mở đúng cuộc trò chuyện. Thông báo Chrome còn báo cả khi đã đóng tab —
        nhưng Chrome vẫn phải đang chạy (không thoát hẳn) và bạn đã bấm "Bật thông báo Chrome".
      </div>
    </div>
  );
}

function NewChat() {
  const { startDirect, startGroup, me } = useChat();
  // Chỉ GĐ/CV hoặc Trưởng phòng DVKH mới lập được nhóm CÓ đối tác -> mới tải kèm đối tác vào danh sách chọn.
  const canPartner = me?.role === 'GD' || me?.role === 'CV' || (me?.deptHeadOf || []).includes('Phòng DVKH');
  const [tab, setTab] = useState('direct'); // 'direct' | 'group'
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState('');
  const [groupName, setGroupName] = useState('');
  const [picked, setPicked] = useState(new Set());
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    chatUsers(canPartner).then(setUsers).catch(() => {});
  }, [canPartner]);

  const filtered = users.filter((u) => u.name.toLowerCase().includes(q.toLowerCase()));
  // RÀNG BUỘC nhóm có đối tác: khi đã chọn đối tác, chỉ cho chọn thêm người NẰM TRONG danh bạ đối tác đó
  // (và ngược lại). Vô hiệu hoá (làm mờ) người không hợp lệ để khỏi tạo nhóm sai.
  const pickedUsers = users.filter((x) => picked.has(x.id));
  const pickedDts = pickedUsers.filter((x) => x.role === 'DT');
  const pickedNonDt = pickedUsers.filter((x) => x.role !== 'DT');
  const isDisabled = (u) => {
    if (picked.has(u.id)) return false; // đã chọn -> luôn cho bỏ chọn
    if (u.role === 'DT') { const allow = new Set(u.partnerContacts || []); return pickedNonDt.some((x) => !allow.has(x.id)); }
    return pickedDts.some((dt) => !(dt.partnerContacts || []).includes(u.id));
  };

  async function doDirect(u) {
    setBusy(true);
    try {
      await startDirect(u.id);
    } finally {
      setBusy(false);
    }
  }
  function togglePick(id) {
    setPicked((prev) => {
      const s = new Set(prev);
      s.has(id) ? s.delete(id) : s.add(id);
      return s;
    });
  }
  async function doGroup() {
    if (!groupName.trim() || picked.size < 1) return;
    setBusy(true);
    try {
      await startGroup({ name: groupName.trim(), memberIds: [...picked] });
    } catch (err) {
      alert(err?.response?.data?.message || 'Tạo nhóm thất bại');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="chatnew">
      <div className="seg" style={{ margin: '4px 0 10px' }}>
        <button className={tab === 'direct' ? 'active' : ''} onClick={() => setTab('direct')}>
          Chat cá nhân
        </button>
        <button className={tab === 'group' ? 'active' : ''} onClick={() => setTab('group')}>
          Tạo nhóm
        </button>
      </div>

      {tab === 'group' && (
        <input style={{ marginBottom: 8 }} value={groupName} onChange={(e) => setGroupName(e.target.value)} placeholder="Tên nhóm (vd: Phòng kinh doanh)" />
      )}
      <input style={{ marginBottom: 8 }} value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Tìm nhân viên…" />

      <div className="chatuserlist">
        {filtered.map((u) => {
          const disabled = tab === 'group' && isDisabled(u);
          return (
            <div
              key={u.id}
              className={'chatuser-row' + (disabled ? ' disabled' : '')}
              onClick={() => { if (disabled) return; tab === 'direct' ? doDirect(u) : togglePick(u.id); }}
              title={disabled ? 'Không nằm trong danh bạ được trao đổi của đối tác đã chọn' : undefined}
            >
              {tab === 'group' && <input type="checkbox" readOnly checked={picked.has(u.id)} disabled={disabled} />}
              <div className="chat-avatar sm"><AvaImg src={u.avatar} name={u.name} /></div>
              <div className="chatuser-main">
                <div className="chatuser-name">{u.name}{u.role === 'DT' && <span className="chattag-partner">Đối tác</span>}</div>
                <div className="chatuser-sub">{(u.departments || []).join(', ') || u.role}</div>
              </div>
            </div>
          );
        })}
        {!filtered.length && <div className="chatempty">Không tìm thấy nhân viên.</div>}
      </div>

      {tab === 'group' && (
        <button className="btn" style={{ marginTop: 10, width: '100%' }} disabled={busy || !groupName.trim() || picked.size < 1} onClick={doGroup}>
          Tạo nhóm ({picked.size})
        </button>
      )}
    </div>
  );
}
